// -----------------------------------------------------------------------
// ProductionCrewDialog — owner spec 2026-07-06 "דף קשר"
// -----------------------------------------------------------------------
// Contact-sheet for a production: role, name, phone, per-day availability,
// direct WhatsApp button per row + a bottom "broadcast" section that
// forwards all crew numbers to the SA's own WhatsApp so they can spin up
// a real group in one paste.
// -----------------------------------------------------------------------
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Trash2, X as XIcon, Loader2, Save, MessageCircle,
  Users, Send, Clipboard, Clock, Pencil, Copy, MapPin,
  ChevronUp, ChevronDown, Paperclip, Car,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { updateProductionCrewRoster, updateProduction, duplicateProduction, uploadCrewDocument, addCrewTaxiOrder, saveCrewTaxiDefaults, deleteTaxiSavedCard } from "./api";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { SendToAllDialog } from "./SendToAllDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const uuid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const onlyDigits = (s) => (s || "").replace(/\D/g, "");
// Israeli-normalised wa.me link. 050… → 972 50…, 972… kept intact.
const waHref = (phone, text) => {
  const d = onlyDigits(phone);
  if (!d) return null;
  const intl = d.startsWith("972") ? d : (d.startsWith("0") ? `972${d.slice(1)}` : `972${d}`);
  const suffix = text ? `?text=${encodeURIComponent(text)}` : "";
  return `https://wa.me/${intl}${suffix}`;
};

// Owner spec 2026-07-08k — Input that auto-shrinks its font-size when the
// content is too wide for the visible field, so long roles/names/phones
// never get truncated by the small column widths in the crew table.
function AutoShrinkInput({ value, className = "", ...rest }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.fontSize = "";
    if (!el.value) return;
    let size = 14;
    let guard = 40;
    while (el.scrollWidth > el.clientWidth + 1 && size > 8 && guard-- > 0) {
      size -= 0.5;
      el.style.fontSize = `${size}px`;
    }
  }, [value]);
  return (
    <Input
      ref={ref}
      value={value}
      className={className}
      {...rest}
    />
  );
}



// Categories match the columns in the SA's Google-sheet contact list.
const CATEGORIES = [
  { key: "cast_directing",  name: "שחקנים / בימוי",         theme: "border-sky-400/50 bg-sky-950/40",        header: "from-sky-600 to-blue-600" },
  { key: "production",      name: "הפקה",                   theme: "border-amber-400/50 bg-amber-950/40",    header: "from-amber-600 to-orange-500" },
  { key: "location_camera", name: "לוקיישן / צילום וציוד",  theme: "border-emerald-400/50 bg-emerald-950/40", header: "from-emerald-600 to-teal-600" },
  { key: "art_wardrobe",    name: "תפאורה, דרס, פרופס",     theme: "border-fuchsia-400/50 bg-fuchsia-950/40", header: "from-fuchsia-600 to-pink-600" },
  { key: "expenses",        name: "הוצאות הפקה",             theme: "border-rose-400/50 bg-rose-950/40",       header: "from-rose-600 to-red-600" },
];
const catByKey = (k) => CATEGORIES.find((c) => c.key === k) || CATEGORIES[0];

const newRow = (category) => ({
  id: uuid(), category, role: "", name: "", phone: "", note: "",
  days: [false, false, false, false, false],
  hours: {}, // owner spec 2026-07-08 — per-day hours, keyed by day index: { "0": {start:"12:00", end:"19:00"} }
  meal_pref: "meat", // owner spec 2026-07-07d — default בשרי
  kosher: "", // owner spec 2026-07-08d — free-text special kashrut (e.g. "מהדרין")
});

// Meal-preference cycle — click cycles through the 4 options.
// Owner spec 2026-07-08n — added "none" as a 4th step. People with
// `meal_pref === "none"` are attending the shoot but NOT getting catering,
// so they are excluded from the catering headcount but still included in
// the overall attending count.
const MEAL_OPTIONS = [
  { key: "meat",  label: "בשרי",    emoji: "🥩", cls: "bg-rose-600 border-rose-400 text-white" },
  { key: "veg",   label: "צמחוני",  emoji: "🥗", cls: "bg-lime-600 border-lime-400 text-white" },
  { key: "vegan", label: "טבעוני",  emoji: "🌱", cls: "bg-emerald-600 border-emerald-400 text-white" },
  { key: "none",  label: "ללא",     emoji: "⛔", cls: "bg-stone-600 border-stone-400 text-white" },
];
const nextMeal = (curr) => {
  const i = MEAL_OPTIONS.findIndex((m) => m.key === (curr || "meat"));
  return MEAL_OPTIONS[(i + 1) % MEAL_OPTIONS.length].key;
};
const mealBadge = (key) => MEAL_OPTIONS.find((m) => m.key === (key || "meat")) || MEAL_OPTIONS[0];

// SA's WhatsApp — auto-populated as the recipient for the broadcast.
const OWNER_WA = "0504944192";

// Compact card that generates a single WhatsApp group — phones only.
function GroupCard({ title, phones, onSend, onCopy, testid, themeCls, highlight }) {
  return (
    <div
      className={`rounded-xl border ${highlight ? "border-white/40 bg-black/40" : "border-white/15 bg-black/30"} overflow-hidden`}
      data-testid={testid}
    >
      <div className={`px-3 py-1.5 bg-gradient-to-l ${themeCls} text-white font-bold text-sm flex items-center justify-between gap-2`}>
        <span>{title}</span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onSend}
            disabled={!phones.length}
            className="inline-flex items-center gap-1 rounded-md bg-black/30 hover:bg-black/50 disabled:opacity-40 px-2 py-1 text-[11px] font-bold"
            data-testid={`${testid}-send`}
            title="שלח לוואטסאפ שלי"
          >
            <Send className="h-3.5 w-3.5" />
            שלח
          </button>
          <button
            type="button"
            onClick={onCopy}
            disabled={!phones.length}
            className="inline-flex items-center gap-1 rounded-md bg-black/30 hover:bg-black/50 disabled:opacity-40 px-2 py-1 text-[11px] font-bold"
            data-testid={`${testid}-copy`}
            title="העתק מספרים"
          >
            <Clipboard className="h-3.5 w-3.5" />
            העתק
          </button>
        </div>
      </div>
      <div className="px-3 py-2 font-mono text-[11px] text-white/90 leading-relaxed break-all min-h-[28px]" dir="ltr">
        {phones.length ? phones.join(", ") : <span className="text-white/40" dir="rtl">אין מספרים בקטגוריה</span>}
      </div>
    </div>
  );
}


export function ProductionCrewDialog({ open, onOpenChange, production, companyId, onSaved }) {
  const [rows, setRows] = useState([]);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  // Owner spec 2026-07-08f — saved WhatsApp template for personal messages.
  // Stored on the production doc under `crew_msg_template`, hydrated here
  // once the dialog opens and pushed via PATCH /productions/{id}.
  const [waTemplate, setWaTemplate] = useState("");
  // Owner spec 2026-07-08m — catering vendors per day (3 slots each).
  const [cateringByDay, setCateringByDay] = useState({});
  const initial = production?.crew_roster;

  // Owner spec 2026-07-08g — hydrate rows ONLY when the dialog opens
  // (once per open per production). Previously the deps also included
  // `initial` (the parent's crew_roster reference), so every successful
  // auto-save that refetched the production would re-run this effect and
  // wipe the SA's in-flight clicks — the user saw their toggle "revert".
  useEffect(() => {
    if (!open) return;
    if (Array.isArray(initial) && initial.length > 0) {
      setRows(JSON.parse(JSON.stringify(initial)));
    } else {
      setRows([]);
    }
    setWaTemplate(production?.crew_msg_template || "");
    setCateringByDay(JSON.parse(JSON.stringify(production?.catering_by_day || {})));
    setSaveStatus("idle");
  }, [open, production?.id]);

  // Owner spec 2026-07-08m — debounced auto-save of catering data.
  const cateringHydrated = useRef(false);
  const lastSavedCateringRef = useRef("");
  useEffect(() => {
    if (!open) { cateringHydrated.current = false; lastSavedCateringRef.current = ""; return; }
    if (!production?.id) return;
    const currentJson = JSON.stringify(cateringByDay);
    if (!cateringHydrated.current) {
      cateringHydrated.current = true;
      lastSavedCateringRef.current = currentJson;
      return;
    }
    if (currentJson === lastSavedCateringRef.current) return;
    const t = setTimeout(async () => {
      try {
        await updateProduction(
          production.id,
          { catering_by_day: cateringByDay },
          companyId
        );
        lastSavedCateringRef.current = currentJson;
        onSaved && onSaved();
      } catch (e) {
        toast.error(e.response?.data?.detail || "שמירת הקיטרינג נכשלה");
      }
    }, 700);
    return () => clearTimeout(t);
  }, [cateringByDay, open, production, companyId, onSaved]);

  const patchCatering = (dayIdx, slot, field, value) => {
    setCateringByDay((prev) => {
      const key = String(dayIdx);
      const day = { ...(prev[key] || {}) };
      const slotObj = { ...(day[slot] || { name: "", phone: "" }) };
      slotObj[field] = value;
      day[slot] = slotObj;
      return { ...prev, [key]: day };
    });
  };

  // Persist a new WhatsApp template on the production doc.
  const saveWaTemplate = async (nextBody) => {
    try {
      await updateProduction(
        production.id,
        { crew_msg_template: nextBody },
        companyId
      );
      setWaTemplate(nextBody);
      onSaved && onSaved();
      toast.success("התבנית נשמרה — תופיע אוטומטית לכל איש צוות הבא");
    } catch (e) {
      toast.error(e.response?.data?.detail || "שמירת התבנית נכשלה");
    }
  };

  // Owner spec 2026-07-07e — auto-save: debounce 700ms after the last
  // keystroke / toggle, PUT the whole roster, and never require the SA
  // to press a save button. First hydration (initial load) is skipped.
  // Owner spec 2026-07-08e — the "saving…" spinner MUST appear ONLY while
  // the request is in-flight (not during the debounce wait), otherwise it
  // looks permanently stuck while the SA is typing.
  // Owner spec 2026-07-08i — the effect must ALSO short-circuit when the
  // rows content is byte-identical to what was already persisted. Parents
  // that don't memoise `onSaved` cause this hook to re-run after every
  // successful save (onSaved triggers a refetch → parent re-render → new
  // onSaved reference → effect re-runs), producing a "נשמר" flash even
  // when the SA did nothing.
  const hydrated = useRef(false);
  const lastSavedRef = useRef("");
  useEffect(() => {
    if (!open) { hydrated.current = false; lastSavedRef.current = ""; return; }
    if (!production?.id) return;
    const currentJson = JSON.stringify(rows);
    if (!hydrated.current) {
      hydrated.current = true;
      lastSavedRef.current = currentJson;
      return;
    }
    if (currentJson === lastSavedRef.current) return; // no actual change
    const t = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        await updateProductionCrewRoster(production.id, rows, companyId);
        lastSavedRef.current = currentJson;
        setSaveStatus("saved");
        onSaved && onSaved();
      } catch (e) {
        setSaveStatus("error");
        toast.error(e.response?.data?.detail || "השמירה האוטומטית נכשלה");
      }
    }, 700);
    return () => clearTimeout(t);
  }, [rows, open, production?.id, companyId, onSaved]);

  // Owner spec 2026-07-08c/h — brief flash of "נשמר ✓" then GONE. Idle
  // renders nothing at all (no dot) so the footer isn't visually stuck.
  useEffect(() => {
    if (saveStatus !== "saved") return;
    const t = setTimeout(() => setSaveStatus("idle"), 600);
    return () => clearTimeout(t);
  }, [saveStatus]);

  // Derive per-category buckets and header days (from schedule if present).
  // Owner spec 2026-07-07b — Hebrew date format DD.M (e.g. 12.7, not 07.12).
  const fmtIL = (iso) => {
    if (!iso) return "";
    const parts = iso.split("-");
    if (parts.length < 3) return iso;
    return `${parseInt(parts[2], 10)}.${parseInt(parts[1], 10)}`;
  };
  // Owner spec 2026-07-08b — Hebrew weekday label (יום א' ‥ יום ז').
  const hebWeekday = (iso) => {
    if (!iso) return "";
    const parts = iso.split("-").map((p) => parseInt(p, 10));
    if (parts.length < 3 || parts.some(isNaN)) return "";
    // JS: 0=Sunday..6=Saturday → Hebrew: יום א'..יום ז'
    const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    if (isNaN(d.getTime())) return "";
    const names = ["יום א׳", "יום ב׳", "יום ג׳", "יום ד׳", "יום ה׳", "יום ו׳", "שבת"];
    return names[d.getUTCDay()] || "";
  };
  const dayHeaders = useMemo(() => {
    const sched = production?.schedule || [];
    if (sched.length > 0) {
      return sched.slice(0, 5).map((d) => ({
        date: fmtIL(d.date) || d.label || "יום",
        weekday: hebWeekday(d.date),
        iso: d.date || "",
      }));
    }
    return [1, 2, 3, 4, 5].map((n) => ({ date: String(n), weekday: "", iso: "" }));
  }, [production?.schedule]);

  const grouped = useMemo(() => {
    const m = {};
    const knownKeys = new Set([
      ...CATEGORIES.map((c) => c.key),
      ...((production?.custom_categories || []).map((c) => c.key)),
    ]);
    knownKeys.forEach((k) => { m[k] = []; });
    for (const r of rows) {
      const cat = knownKeys.has(r.category) ? r.category : CATEGORIES[0].key;
      (m[cat] = m[cat] || []).push(r);
    }
    return m;
  }, [rows, production?.custom_categories]);

  const addRow = (category) => setRows((prev) => [...prev, newRow(category)]);
  const patchRow = (id, patch) =>
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));
  const deleteRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
  // Owner spec 2026-07-08j — clone an entire row (fresh id, inserted
  // immediately after the source row).
  const duplicateRow = (id) =>
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === id);
      if (idx < 0) return prev;
      const src = prev[idx];
      const clone = {
        ...JSON.parse(JSON.stringify(src)),
        id: uuid(),
        name: src.name ? `${src.name} (עותק)` : "",
      };
      const next = [...prev];
      next.splice(idx + 1, 0, clone);
      return next;
    });

  // Owner spec 2026-07-08q — reorder a row up/down within its category.
  // Direction: -1 = up, +1 = down. Only swaps with the nearest sibling
  // that shares the same category (skips rows from other categories in
  // the flat rows[] array).
  const moveRow = (id, direction) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === id);
      if (idx < 0) return prev;
      const cat = prev[idx].category;
      const step = direction > 0 ? 1 : -1;
      let j = idx + step;
      while (j >= 0 && j < prev.length && prev[j].category !== cat) j += step;
      if (j < 0 || j >= prev.length) return prev; // already at the end
      const next = [...prev];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  // Owner spec 2026-07-08k — effective category list combines the 5
  // built-ins with any SA-created custom duplicates, and applies name
  // overrides so the SA can rename any block.
  const effectiveCategories = useMemo(() => {
    const overrides = production?.category_names || {};
    const customs = production?.custom_categories || [];
    const withOverrides = CATEGORIES.map((c) => ({
      ...c,
      name: overrides[c.key] || c.name,
      isCustom: false,
    }));
    const themeByKey = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));
    const customBlocks = customs.map((cc) => {
      const themeSrc = themeByKey[cc.theme_key] || CATEGORIES[0];
      return {
        key: cc.key,
        name: overrides[cc.key] || cc.name,
        theme: themeSrc.theme,
        header: themeSrc.header,
        isCustom: true,
      };
    });
    return [...withOverrides, ...customBlocks];
  }, [production?.category_names, production?.custom_categories]);

  // Persist a `production` patch (used by category rename / duplicate).
  // Owner spec 2026-07-08p — send ONLY the fields being changed so we
  // never overwrite an unrelated field with stale local data.
  const patchProduction = async (patch) => {
    try {
      await updateProduction(production.id, patch, companyId);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "השמירה נכשלה");
      throw e;
    }
  };

  const renameCategory = async (key) => {
    const current =
      (production?.category_names || {})[key] ||
      effectiveCategories.find((c) => c.key === key)?.name ||
      "";
    const next = window.prompt("שם קטגוריה חדש:", current);
    if (next == null) return; // user cancelled
    const trimmed = next.trim();
    if (!trimmed || trimmed === current) return;
    const nextOverrides = { ...(production?.category_names || {}), [key]: trimmed };
    await patchProduction({ category_names: nextOverrides });
    toast.success("שם הקטגוריה עודכן");
  };

  const duplicateCategoryBlock = async (sourceKey) => {
    const src = effectiveCategories.find((c) => c.key === sourceKey);
    if (!src) return;
    if (!window.confirm(`לשכפל את הקטגוריה "${src.name}" יחד עם ${(grouped[sourceKey] || []).length} השורות שלה?`)) return;
    const newKey = uuid();
    const newCustom = {
      key: newKey,
      name: `${src.name} (עותק)`,
      theme_key: src.isCustom
        ? (production?.custom_categories || []).find((c) => c.key === sourceKey)?.theme_key || "cast_directing"
        : sourceKey,
    };
    const nextCustoms = [...(production?.custom_categories || []), newCustom];
    // Clone rows locally — auto-save picks them up.
    const clonedRows = (grouped[sourceKey] || []).map((r) => ({
      ...JSON.parse(JSON.stringify(r)),
      id: uuid(),
      category: newKey,
    }));
    setRows((prev) => [...prev, ...clonedRows]);
    try {
      await patchProduction({ custom_categories: nextCustoms });
      toast.success("הקטגוריה שוכפלה");
    } catch (_) { /* toast in patchProduction */ }
  };

  const deleteCustomCategory = async (key) => {
    if (!window.confirm("למחוק את הקטגוריה הזו ואת כל השורות שבה?")) return;
    const nextCustoms = (production?.custom_categories || []).filter((c) => c.key !== key);
    const nextOverrides = { ...(production?.category_names || {}) };
    delete nextOverrides[key];
    setRows((prev) => prev.filter((r) => r.category !== key));
    try {
      await patchProduction({ custom_categories: nextCustoms, category_names: nextOverrides });
      toast.success("הקטגוריה נמחקה");
    } catch (_) { /* toast in patchProduction */ }
  };

  // ---- Broadcast helpers -------------------------------------------------
  // Owner spec 2026-07-06b — one card per category + one "all-together"
  // card. Each card shows ONLY the phone numbers (comma-separated) that
  // the SA needs to paste into a fresh WhatsApp group.
  const normPhone = (p) => {
    const d = (p || "").replace(/\D/g, "");
    if (!d) return "";
    // Return with Israeli 0-prefix for pasting into WhatsApp contacts.
    if (d.startsWith("972")) return `0${d.slice(3)}`;
    if (d.startsWith("0")) return d;
    return `0${d}`;
  };
  const categoryPhones = useMemo(() => {
    const out = {};
    for (const cat of CATEGORIES) {
      const list = (grouped[cat.key] || [])
        .map((r) => normPhone(r.phone))
        .filter(Boolean);
      out[cat.key] = Array.from(new Set(list)); // dedup
    }
    return out;
  }, [grouped]);
  const allPhones = useMemo(() => {
    const set = new Set();
    for (const cat of CATEGORIES) {
      for (const p of (categoryPhones[cat.key] || [])) set.add(p);
    }
    return Array.from(set);
  }, [categoryPhones]);

  const openWaWithText = (phones) => {
    if (!phones.length) { toast.error("אין מספרים בקטגוריה"); return; }
    // Owner spec 2026-07-07d — send phones only, no header text.
    const body = phones.join(", ");
    const link = waHref(OWNER_WA, body);
    if (!link) { toast.error("אין מספר יעד"); return; }
    window.open(link, "_blank");
  };
  const copyPhones = async (phones) => {
    if (!phones.length) { toast.error("אין מספרים להעתקה"); return; }
    try {
      await navigator.clipboard.writeText(phones.join(", "));
      toast.success(`${phones.length} מספרים הועתקו`);
    } catch { toast.error("העתקה נכשלה"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="ff-crew-dialog">
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-400" />
            <span className="bg-gradient-to-l from-emerald-400 to-teal-400 bg-clip-text text-transparent">
              דף קשר — צוות הפקה
            </span>
            {production?.name && (
              <span className="text-xs text-stone-400 font-normal">· {production.name}</span>
            )}
          </DialogTitle>
          <DialogDescription className="text-right text-stone-400 text-xs">
            {rows.length} אנשי קשר · לחיצה על סמל WhatsApp פותחת שיחה ישירה
          </DialogDescription>
        </DialogHeader>

        {/* Owner spec 2026-07-08h — production-level toolbar inside the crew
            dialog: quick edit + duplicate + inline location input, so the SA
            never has to close this dialog to update the production card. */}
        <ProductionToolbar
          production={production}
          companyId={companyId}
          dayHeaders={dayHeaders}
          onSaved={onSaved}
          onClose={() => onOpenChange(false)}
        />

        <div className="space-y-4 pt-1">
          {effectiveCategories.map((cat) => (
            <CategoryBlock
              key={cat.key}
              category={cat}
              rows={grouped[cat.key] || []}
              dayHeaders={dayHeaders}
              prodName={production?.name}
              production={production}
              companyId={companyId}
              waTemplate={waTemplate}
              onSaveTemplate={saveWaTemplate}
              allCategories={effectiveCategories}
              allRows={rows}
              onAdd={() => addRow(cat.key)}
              onPatch={patchRow}
              onDuplicate={duplicateRow}
              onDelete={deleteRow}
              onMove={moveRow}
              onRenameCategory={() => renameCategory(cat.key)}
              onDuplicateCategory={() => duplicateCategoryBlock(cat.key)}
              onDeleteCategory={cat.isCustom ? () => deleteCustomCategory(cat.key) : null}
            />
          ))}

          {/* Grand-total headcount across all categories per day. */}
          <div className="rounded-2xl border-2 border-white/25 bg-gradient-to-l from-stone-800 to-stone-900 p-4" data-testid="ff-crew-grand-total">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-amber-300" />
              <h3 className="font-heading text-lg font-extrabold text-white">
                סה״כ באים ליום — כל הקטגוריות
              </h3>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {[0, 1, 2, 3, 4].map((i) => {
                const dayRows = effectiveCategories.flatMap((cat) =>
                  (grouped[cat.key] || []).filter((r) => r.days?.[i] && (r.name || r.phone))
                );
                const totalAll = dayRows.length;
                const meat  = dayRows.filter((r) => (r.meal_pref || "meat") === "meat").length;
                const veg   = dayRows.filter((r) => r.meal_pref === "veg").length;
                const vegan = dayRows.filter((r) => r.meal_pref === "vegan").length;
                // Owner spec 2026-07-08n — "ללא" people don't consume
                // catering. Catering headcount excludes them.
                const noneCount = dayRows.filter((r) => r.meal_pref === "none").length;
                const totalCatering = totalAll - noneCount;
                // Owner spec 2026-07-08d — aggregate free-text kashrut per day.
                // Kashrut is only relevant for people who actually eat.
                const kosherCounts = {};
                for (const r of dayRows) {
                  if (r.meal_pref === "none") continue;
                  const k = (r.kosher || "").trim();
                  if (!k) continue;
                  kosherCounts[k] = (kosherCounts[k] || 0) + 1;
                }
                return (
                  <div key={i} className="rounded-xl bg-amber-500/20 border border-amber-400/40 text-center py-2 px-1 space-y-1">
                    {/* Owner spec 2026-07-08n — total crew (including "ללא")
                        rendered as the BIG headline number above the cube;
                        catering count moves INSIDE the cube so the SA can
                        tell "who is on set" from "who eats" at a glance. */}
                    <div className="rounded-lg bg-black/40 border border-amber-400/25 py-1">
                      <div className="text-[9px] text-amber-300/90 font-bold uppercase tracking-wider">סה״כ צוות כללי</div>
                      <div className="text-xl font-extrabold text-amber-100 tabular-nums" data-testid={`ff-crew-grand-total-all-${i}`}>{totalAll}</div>
                    </div>
                    {/* Owner spec 2026-07-08m — catering vendors ABOVE the
                        day summary. Auto-saves via ProductionCrewDialog's
                        debounced catering effect. */}
                    <CateringSlots
                      dayIdx={i}
                      catering={cateringByDay[String(i)] || cateringByDay[i] || {}}
                      onPatch={patchCatering}
                    />
                    <div className="text-[10px] text-amber-100/80 font-bold">{dayHeaders[i]?.date || `יום ${i + 1}`}</div>
                    {dayHeaders[i]?.weekday && (
                      <div className="text-[9px] text-amber-200 font-extrabold" data-testid={`ff-crew-grand-weekday-${i}`}>
                        {dayHeaders[i].weekday}
                      </div>
                    )}
                    <div className="text-2xl font-extrabold text-amber-100 tabular-nums" data-testid={`ff-crew-grand-day-${i}`}>{totalCatering}</div>
                    <div className="text-[10px] text-amber-100/70">אנשי צוות הסעדה</div>
                    <div className="pt-1 border-t border-amber-400/25 grid grid-cols-4 gap-0.5 text-[10px] font-bold" data-testid={`ff-crew-meal-breakdown-${i}`}>
                      <div className="rounded bg-rose-600/40 text-white py-0.5" title="בשרי">🥩 {meat}</div>
                      <div className="rounded bg-lime-600/40 text-white py-0.5" title="צמחוני">🥗 {veg}</div>
                      <div className="rounded bg-emerald-600/40 text-white py-0.5" title="טבעוני">🌱 {vegan}</div>
                      <div className="rounded bg-stone-600/50 text-white py-0.5" title="ללא הסעדה">⛔ {noneCount}</div>
                    </div>
                    {Object.keys(kosherCounts).length > 0 && (
                      <div className="pt-1 border-t border-amber-400/25 space-y-0.5" data-testid={`ff-crew-kosher-breakdown-${i}`}>
                        {Object.entries(kosherCounts).map(([label, n]) => (
                          <div key={label} className="rounded bg-violet-600/40 text-white text-[10px] font-extrabold py-0.5 px-1 truncate" title={`מנה ${label} × ${n}`}>
                            מנה {label} {n}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* CRM-style multi-group generator — one card per category
              plus a combined "everyone together" card. Each shows
              phone numbers only, ready to paste into a new WhatsApp
              group at flow-up.co.il. */}
          <div className="rounded-2xl border-2 border-emerald-500/40 bg-emerald-950/50 p-4 space-y-3" data-testid="ff-crew-broadcast">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-emerald-400" />
              <h3 className="font-heading text-lg font-extrabold text-emerald-100">
                יצירת קבוצות WhatsApp — {production?.name || "הפקה"}
              </h3>
            </div>
            <p className="text-xs text-emerald-100/80">
              קבוצה לכל קטגוריה + קבוצה משותפת. לחיצה על &quot;שלח לוואטסאפ שלי&quot; תפתח וואטסאפ עם רשימת המספרים בלבד (מוכן להדבקה). הוואטסאפ שלך: <span dir="ltr">{OWNER_WA}</span>.
            </p>

            {effectiveCategories.map((cat) => {
              const phones = categoryPhones[cat.key] || [];
              return (
                <GroupCard
                  key={cat.key}
                  title={`${cat.name} · ${phones.length} מספרים`}
                  phones={phones}
                  onSend={() => openWaWithText(phones)}
                  onCopy={() => copyPhones(phones)}
                  testid={`ff-crew-group-${cat.key}`}
                  themeCls={cat.header}
                />
              );
            })}

            {/* Everyone-together card */}
            <GroupCard
              title={`כולם ביחד · ${allPhones.length} מספרים`}
              phones={allPhones}
              onSend={() => openWaWithText(allPhones)}
              onCopy={() => copyPhones(allPhones)}
              testid="ff-crew-group-all"
              themeCls="from-emerald-600 to-teal-500"
              highlight
            />
          </div>

          <div className="flex items-center justify-between gap-2 pt-2 sticky bottom-0 bg-stone-900/95 backdrop-blur border-t border-white/10 py-2">
            {/* Owner spec 2026-07-08h — completely silent when idle. */}
            <div className="flex items-center gap-2 text-xs font-bold min-h-[20px]" data-testid="ff-crew-autosave-status">
              {saveStatus === "saving" && (<><Loader2 className="h-4 w-4 animate-spin text-amber-400" /><span className="text-amber-200">שומר…</span></>)}
              {saveStatus === "saved"  && (<><Save className="h-4 w-4 text-emerald-400" /><span className="text-emerald-200">נשמר ✓</span></>)}
              {saveStatus === "error"  && (<span className="text-rose-300">שמירה נכשלה — ננסה שוב בשינוי הבא</span>)}
            </div>
            <Button onClick={() => onOpenChange(false)} className="bg-stone-700 hover:bg-stone-600 text-white font-bold" data-testid="ff-crew-close">
              סגור
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CategoryBlock({ category, rows, dayHeaders, prodName, production, waTemplate, onSaveTemplate, allCategories, allRows, companyId, onAdd, onPatch, onDuplicate, onDelete, onMove, onRenameCategory, onDuplicateCategory, onDeleteCategory }) {
  // Owner spec 2026-07-07 — per-day headcount + copy-names / copy-phones.
  const dayTotals = [0, 1, 2, 3, 4].map((i) =>
    rows.filter((r) => (r.days?.[i]) && (r.name || r.phone)).length
  );
  const namesOnly = rows.map((r) => (r.name || "").trim()).filter(Boolean);
  const phonesOnly = rows
    .map((r) => {
      const d = (r.phone || "").replace(/\D/g, "");
      if (!d) return "";
      if (d.startsWith("972")) return `0${d.slice(3)}`;
      if (d.startsWith("0")) return d;
      return `0${d}`;
    })
    .filter(Boolean);
  const copyList = async (list, label) => {
    if (!list.length) { toast.error(`אין ${label} להעתקה`); return; }
    try {
      await navigator.clipboard.writeText(list.join(", "));
      toast.success(`${list.length} ${label} הועתקו`);
    } catch { toast.error("העתקה נכשלה"); }
  };
  // Owner spec 2026-02-08c — CATEGORY-scoped broadcast. We synthesize a
  // pseudo-production that only carries the crew rows of this category,
  // so SendToAllDialog auto-populates ONLY these people in the
  // recipient list. `additional_contacts` is emptied by design.
  const [catBroadcastOpen, setCatBroadcastOpen] = useState(false);
  const scopedProduction = useMemo(() => ({
    ...production,
    crew_roster: rows,
    additional_contacts: [],
    _categoryName: category.name,
  }), [production, rows, category.name]);
  return (
    <div className={`rounded-2xl border-2 ${category.theme} overflow-hidden`} data-testid={`ff-crew-cat-${category.key}`}>
      <div className={`px-4 py-2 bg-gradient-to-l ${category.header} text-white font-heading text-lg font-extrabold flex items-center justify-between gap-2 flex-wrap`}>
        <div className="flex items-center gap-2">
          <span>{category.name}</span>
          <span className="text-xs opacity-80 font-normal">({rows.length})</span>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Owner spec 2026-02-08c — CATEGORY-level WhatsApp broadcast.
              Only sends to the crew members within THIS category (e.g.
              only "בשרי" or only "פרסטיז"). Placed FIRST (right of the
              category title in RTL) so it's the most prominent action. */}
          <button
            type="button"
            onClick={() => setCatBroadcastOpen(true)}
            className="inline-flex items-center gap-1 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black px-2 py-1 text-[11px] font-extrabold shadow-md"
            data-testid={`ff-crew-broadcast-cat-${category.key}`}
            title={`שלח וואטסאפ לכל אנשי הצוות ב-${category.name}`}
          >
            <WhatsAppIcon className="h-3.5 w-3.5" />
            שלח וואטסאפ
          </button>
          {/* Owner spec 2026-07-08k — rename / duplicate / delete category. */}
          {onRenameCategory && (
            <button
              type="button"
              onClick={onRenameCategory}
              className="inline-flex items-center gap-1 rounded-md bg-white/15 hover:bg-white/30 px-2 py-1 text-[11px] font-bold"
              data-testid={`ff-crew-rename-cat-${category.key}`}
              title="שנה שם קטגוריה"
            >
              <Pencil className="h-3.5 w-3.5" />
              ערוך
            </button>
          )}
          {onDuplicateCategory && (
            <button
              type="button"
              onClick={onDuplicateCategory}
              className="inline-flex items-center gap-1 rounded-md bg-white/15 hover:bg-white/30 px-2 py-1 text-[11px] font-bold"
              data-testid={`ff-crew-dup-cat-${category.key}`}
              title="שכפל קטגוריה עם כל השורות"
            >
              <Copy className="h-3.5 w-3.5" />
              שכפל
            </button>
          )}
          {onDeleteCategory && (
            <button
              type="button"
              onClick={onDeleteCategory}
              className="inline-flex items-center gap-1 rounded-md bg-rose-600/60 hover:bg-rose-500 px-2 py-1 text-[11px] font-bold"
              data-testid={`ff-crew-del-cat-${category.key}`}
              title="מחק קטגוריה"
            >
              <Trash2 className="h-3.5 w-3.5" />
              מחק
            </button>
          )}
          <button
            type="button"
            onClick={() => copyList(namesOnly, "שמות")}
            className="inline-flex items-center gap-1 rounded-md bg-black/30 hover:bg-black/50 px-2 py-1 text-[11px] font-bold"
            data-testid={`ff-crew-copy-names-${category.key}`}
            title="העתק את כל השמות בקטגוריה"
          >
            <Clipboard className="h-3.5 w-3.5" />
            העתק שמות
          </button>
          <button
            type="button"
            onClick={() => copyList(phonesOnly, "מספרים")}
            className="inline-flex items-center gap-1 rounded-md bg-black/30 hover:bg-black/50 px-2 py-1 text-[11px] font-bold"
            data-testid={`ff-crew-copy-phones-${category.key}`}
            title="העתק את כל המספרים בקטגוריה"
          >
            <Clipboard className="h-3.5 w-3.5" />
            העתק מספרים
          </button>
        </div>
      </div>
      <div className="p-3 space-y-1.5">
        <div className="grid grid-cols-[24px,minmax(100px,1fr),minmax(100px,1fr),minmax(110px,1.1fr),repeat(5,60px),32px,32px,56px,minmax(80px,1fr),100px] gap-2 items-center text-[10px] uppercase tracking-widest font-bold text-white/60 px-1">
          <div />
          <div>תפקיד</div>
          <div>איש קשר</div>
          <div>טלפון</div>
          {dayHeaders.slice(0, 5).map((h, i) => {
            // Owner spec 2026-07-08j — per-date location shown under the
            // weekday. Falls back to production.location when unset.
            const perDayLoc =
              production?.location_by_day?.[i] ??
              production?.location_by_day?.[String(i)] ??
              "";
            const shownLoc = perDayLoc || production?.location || "";
            const isDefault = !perDayLoc && !!production?.location;
            return (
              <div key={i} className="text-center leading-tight">
                <div>{h.date}</div>
                {h.weekday && (
                  <div className="text-[9px] text-amber-300/80 font-extrabold normal-case tracking-normal mt-0.5" data-testid={`ff-crew-header-weekday-${i}`}>
                    {h.weekday}
                  </div>
                )}
                {shownLoc && (
                  <div
                    className={`text-[8px] font-bold normal-case tracking-normal mt-0.5 break-words leading-snug ${isDefault ? "text-stone-500" : "text-emerald-300"}`}
                    title={shownLoc}
                    data-testid={`ff-crew-header-loc-${i}`}
                  >
                    {shownLoc}
                  </div>
                )}
              </div>
            );
          })}
          <div className="text-center">WA</div>
          <div className="text-center">שעות</div>
          <div className="text-center">מנה</div>
          <div className="text-center">כשרות</div>
          <div />
        </div>
        {rows.map((r, idx) => (
          <CrewRow key={r.id} row={r} prodName={prodName} production={production} companyId={companyId} dayHeaders={dayHeaders} waTemplate={waTemplate} onSaveTemplate={onSaveTemplate} allCategories={allCategories} allRows={allRows} onPatch={(p) => onPatch(r.id, p)} onDuplicate={() => onDuplicate(r.id)} onDelete={() => onDelete(r.id)} onMove={onMove ? (dir) => onMove(r.id, dir) : null} canMoveUp={idx > 0} canMoveDown={idx < rows.length - 1} />
        ))}
        {/* Per-day headcount footer */}
        <div className="grid grid-cols-[24px,minmax(100px,1fr),minmax(100px,1fr),minmax(110px,1.1fr),repeat(5,60px),32px,32px,56px,minmax(80px,1fr),100px] gap-2 items-center pt-1.5 mt-1 border-t border-white/15">
          <div />
          <div className="col-span-3 text-[11px] font-bold text-white/70 uppercase tracking-widest px-1">סיכום יומי</div>
          {dayTotals.map((n, i) => (
            <div key={i} className={`text-center text-[12px] font-extrabold tabular-nums rounded-md py-0.5 ${n > 0 ? "bg-amber-500/30 text-amber-100" : "bg-black/20 text-white/30"}`} data-testid={`ff-crew-daytotal-${category.key}-${i}`}>
              {n}
            </div>
          ))}
          <div />
          <div />
          <div />
          <div />
          <div />
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1 rounded-lg border border-dashed border-white/25 bg-white/5 hover:bg-white/10 text-stone-200 text-[11px] font-bold px-2 py-1 transition"
          data-testid={`ff-crew-add-${category.key}`}
        >
          <Plus className="h-3.5 w-3.5" />
          הוסף שורה
        </button>
      </div>
      <SendToAllDialog
        open={catBroadcastOpen}
        onOpenChange={setCatBroadcastOpen}
        production={scopedProduction}
      />
    </div>
  );
}

function CrewRow({ row, prodName, production, companyId, dayHeaders, waTemplate, onSaveTemplate, allCategories, allRows, onPatch, onDuplicate, onDelete, onMove, canMoveUp, canMoveDown }) {
  const [hoursOpen, setHoursOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [taxiOpen, setTaxiOpen] = useState(false);
  const toggleDay = (i) => {
    const next = [...(row.days || [])];
    while (next.length < 5) next.push(false);
    next[i] = !next[i];
    onPatch({ days: next });
  };
  const meal = mealBadge(row.meal_pref);
  const hasAnyHours = Object.values(row.hours || {}).some((h) => h && (h.start || h.end));
  // Format compact hours label for the day tile: "12-19" or "12:30-19:00"
  const compactHours = (h) => {
    if (!h) return "";
    const trim = (s) => (s || "").replace(/:00$/, "");
    const s = trim(h.start);
    const e = trim(h.end);
    if (!s && !e) return "";
    return `${s}-${e}`;
  };
  return (
    <>
    <div className="grid grid-cols-[24px,minmax(100px,1fr),minmax(100px,1fr),minmax(110px,1.1fr),repeat(5,60px),32px,32px,56px,minmax(80px,1fr),100px] gap-2 items-center" data-testid={`ff-crew-row-${row.id}`}>
      {/* Owner spec 2026-07-08o — quick "move to another category" dropdown.
          Native <select> styled as a chevron; on change patches row.category
          which the auto-save picks up. */}
      <div className="relative flex items-center justify-center">
        <select
          value={row.category || ""}
          onChange={(e) => onPatch({ category: e.target.value })}
          className="appearance-none bg-white/10 hover:bg-white/25 border border-white/20 text-transparent rounded w-5 h-5 text-[10px] cursor-pointer focus:outline-none focus:ring-1 focus:ring-emerald-400"
          title="העבר לקטגוריה אחרת"
          data-testid={`ff-crew-move-${row.id}`}
        >
          {(allCategories || []).map((c) => (
            <option key={c.key} value={c.key} className="text-stone-100 bg-stone-900">
              {c.name}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-white/70 text-xs font-bold">▾</span>
      </div>
      <AutoShrinkInput value={row.role}  onChange={(e) => onPatch({ role: e.target.value })}  placeholder="תפקיד" className="bg-stone-800 border-white/10 text-stone-100 h-8 text-sm" data-testid={`ff-crew-role-${row.id}`} />
      <AutoShrinkInput value={row.name}  onChange={(e) => onPatch({ name: e.target.value })}  placeholder="שם"    className="bg-stone-800 border-white/10 text-stone-100 h-8 text-sm" data-testid={`ff-crew-name-${row.id}`} />
      <AutoShrinkInput value={row.phone} onChange={(e) => onPatch({ phone: e.target.value })} placeholder="טלפון" className="bg-stone-800 border-white/10 text-stone-100 h-8 text-sm text-left" dir="ltr" data-testid={`ff-crew-phone-${row.id}`} />
      {[0, 1, 2, 3, 4].map((i) => {
        const active = !!row.days?.[i];
        const hLabel = active ? compactHours(row.hours?.[i]) : "";
        return (
          <button
            key={i}
            type="button"
            onClick={() => toggleDay(i)}
            className={`h-11 w-full rounded-md border font-bold text-xs transition flex flex-col items-center justify-center leading-tight px-0.5 ${active ? "bg-amber-500 border-amber-400 text-black" : "bg-black/30 border-white/20 text-white/40 hover:border-white/50"}`}
            title={`יום ${i + 1}${hLabel ? ` · ${hLabel}` : ""}`}
            data-testid={`ff-crew-day-${row.id}-${i}`}
          >
            <span className="text-sm">{active ? "✓" : ""}</span>
            {hLabel && (
              <span className="text-[9px] font-extrabold tabular-nums mt-0.5" data-testid={`ff-crew-day-hours-${row.id}-${i}`}>
                {hLabel}
              </span>
            )}
          </button>
        );
      })}
      {/* Owner spec 2026-07-08f — WA button now opens a template picker
          dialog (personal saved template OR auto-schedule message). */}
      <button
        type="button"
        onClick={() => {
          if (!row.phone) { toast.error("אין מספר טלפון לאיש הצוות"); return; }
          setWaOpen(true);
        }}
        className={`h-8 w-8 rounded-md flex items-center justify-center transition ${row.phone ? "bg-emerald-600/70 hover:bg-emerald-500 text-white" : "bg-stone-800 text-stone-600 cursor-not-allowed"}`}
        title="WhatsApp — בחר תבנית הודעה"
        data-testid={`ff-crew-wa-${row.id}`}
      >
        <MessageCircle className="h-4 w-4" />
      </button>
      {/* Owner spec 2026-07-08 — per-crew-member hours popup covering all 5 days. */}
      <button
        type="button"
        onClick={() => setHoursOpen(true)}
        className={`h-8 w-8 rounded-md flex items-center justify-center transition border ${hasAnyHours ? "bg-amber-500 border-amber-400 text-black" : "bg-black/30 border-white/20 text-white/70 hover:border-white/50"}`}
        title="ערוך שעות לכל תאריך"
        data-testid={`ff-crew-hours-btn-${row.id}`}
      >
        <Clock className="h-4 w-4" />
      </button>
      {/* Meal-preference cycle button — click cycles through 🥩→🥗→🌱. Owner spec 2026-07-08d: show label under emoji. */}
      <button
        type="button"
        onClick={() => onPatch({ meal_pref: nextMeal(row.meal_pref) })}
        className={`h-11 w-full rounded-md border font-bold text-[9px] transition flex flex-col items-center justify-center leading-tight px-0.5 ${meal.cls}`}
        title={`העדפת ארוחה: ${meal.label} (לחיצה לשינוי)`}
        data-testid={`ff-crew-meal-${row.id}`}
      >
        <span className="text-base leading-none">{meal.emoji}</span>
        <span className="text-[9px] font-extrabold mt-0.5">{meal.label}</span>
      </button>
      {/* Owner spec 2026-07-08d — free-text kashrut (e.g. "מהדרין"). */}
      <AutoShrinkInput
        value={row.kosher || ""}
        onChange={(e) => onPatch({ kosher: e.target.value })}
        placeholder="כשרות"
        className="bg-stone-800 border-white/10 text-stone-100 h-8 text-sm text-center"
        data-testid={`ff-crew-kosher-${row.id}`}
      />
      <div className="flex items-center gap-0.5 justify-center">
        {/* Owner spec 2026-07-08q — move row up / down within category. */}
        <div className="flex flex-col items-center">
          <button
            type="button"
            onClick={() => onMove && onMove(-1)}
            disabled={!canMoveUp || !onMove}
            className="text-white/70 hover:text-white hover:bg-white/15 rounded disabled:opacity-25 disabled:hover:bg-transparent transition"
            title="הזז למעלה"
            data-testid={`ff-crew-move-up-${row.id}`}
          >
            <ChevronUp className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => onMove && onMove(+1)}
            disabled={!canMoveDown || !onMove}
            className="text-white/70 hover:text-white hover:bg-white/15 rounded disabled:opacity-25 disabled:hover:bg-transparent transition"
            title="הזז למטה"
            data-testid={`ff-crew-move-down-${row.id}`}
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
        {/* Owner spec 2026-07-08t — attach & send documents (quote/invoice). */}
        <button
          type="button"
          onClick={() => setDocsOpen(true)}
          className="text-amber-300 hover:text-white hover:bg-amber-500/40 rounded p-1 transition"
          aria-label="מסמכים"
          title="הצעת מחיר / חשבונית"
          data-testid={`ff-crew-docs-${row.id}`}
        >
          <Paperclip className="h-3.5 w-3.5" />
        </button>
        {/* Owner spec 2026-02-07 — book a taxi for this crew member.
            Bright-blue dot appears when at least one taxi has already
            been ordered (row.taxi_orders is a server-persisted list). */}
        <button
          type="button"
          onClick={() => setTaxiOpen(true)}
          className="relative text-yellow-300 hover:text-white hover:bg-yellow-500/40 rounded p-1 transition"
          aria-label="הזמנת מונית"
          title={
            (row.taxi_orders?.length || 0) > 0
              ? `מונית הוזמנה (${row.taxi_orders.length})`
              : "הזמנת מונית"
          }
          data-testid={`ff-crew-taxi-${row.id}`}
        >
          <Car className="h-3.5 w-3.5" />
          {(row.taxi_orders?.length || 0) > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-sky-400 ring-2 ring-stone-900 shadow-[0_0_8px_rgba(56,189,248,0.9)] animate-pulse"
              data-testid={`ff-crew-taxi-dot-${row.id}`}
            />
          )}
        </button>
        <button
          type="button"
          onClick={onDuplicate}
          className="text-indigo-300 hover:text-white hover:bg-indigo-500/60 rounded p-1 transition"
          aria-label="שכפל שורה"
          title="שכפל שורה"
          data-testid={`ff-crew-dup-${row.id}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/25 rounded p-1 transition"
          aria-label="מחק שורה"
          data-testid={`ff-crew-del-${row.id}`}
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
    <CrewHoursDialog
      open={hoursOpen}
      onOpenChange={setHoursOpen}
      row={row}
      dayHeaders={dayHeaders}
      onSave={(hours) => onPatch({ hours })}
    />
    <WhatsAppSendDialog
      open={waOpen}
      onOpenChange={setWaOpen}
      row={row}
      prodName={prodName}
      production={production}
      dayHeaders={dayHeaders}
      template={waTemplate}
      onSaveTemplate={onSaveTemplate}
    />
    <CrewDocumentsDialog
      open={docsOpen}
      onOpenChange={setDocsOpen}
      row={row}
      production={production}
      companyId={companyId}
      allRows={allRows}
    />
    <TaxiOrderDialog
      open={taxiOpen}
      onOpenChange={setTaxiOpen}
      row={row}
      production={production}
    />
    </>
  );
}

// -----------------------------------------------------------------------
// CrewHoursDialog — owner spec 2026-07-08
// Popup listing all 5 production dates with start/end hour inputs per date
// for a single crew member. Saves back a `hours` map keyed by day index.
// -----------------------------------------------------------------------
function CrewHoursDialog({ open, onOpenChange, row, dayHeaders, onSave }) {
  const [draft, setDraft] = useState({});
  // Owner spec 2026-07-08c — Seed the draft ONCE per open (and per row.id),
  // not on every parent re-render. Otherwise the debounced auto-save's
  // rows-reference change wipes the user's live time input.
  useEffect(() => {
    if (!open) return;
    const seed = {};
    for (let i = 0; i < 5; i++) {
      const h = row?.hours?.[i] || row?.hours?.[String(i)] || {};
      seed[i] = { start: h.start || "", end: h.end || "" };
    }
    setDraft(seed);
  }, [open, row?.id]);

  const setField = (i, key, val) =>
    setDraft((prev) => ({ ...prev, [i]: { ...(prev[i] || {}), [key]: val } }));

  const handleConfirm = () => {
    // Drop days that have neither start nor end.
    const clean = {};
    for (let i = 0; i < 5; i++) {
      const s = (draft[i]?.start || "").trim();
      const e = (draft[i]?.end || "").trim();
      if (s || e) clean[i] = { start: s, end: e };
    }
    onSave(clean);
    toast.success("השעות עודכנו");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-md" data-testid={`ff-crew-hours-dialog-${row?.id}`}>
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-400" />
            <span className="bg-gradient-to-l from-amber-300 to-orange-300 bg-clip-text text-transparent">
              שעות עבודה — {row?.name || "איש צוות"}
            </span>
          </DialogTitle>
          <DialogDescription className="text-right text-stone-400 text-xs">
            הזן/י שעת התחלה וסיום לכל תאריך שבו איש הצוות מגיע. השעות יופיעו מתחת לקוביית הווי הצהובה.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 pt-1">
          {[0, 1, 2, 3, 4].map((i) => {
            const header = dayHeaders?.[i] || { date: `יום ${i + 1}`, weekday: "" };
            const attending = !!row?.days?.[i];
            return (
              <div
                key={i}
                className={`grid grid-cols-[72px,1fr,1fr,28px] gap-2 items-center rounded-lg border p-2 ${attending ? "bg-amber-950/30 border-amber-500/40" : "bg-black/20 border-white/10"}`}
                data-testid={`ff-crew-hours-row-${row?.id}-${i}`}
              >
                <div className="text-center leading-tight">
                  <div className={`text-[12px] font-extrabold ${attending ? "text-amber-200" : "text-white/70"}`}>
                    {header.date}
                  </div>
                  {header.weekday && (
                    <div className={`text-[10px] font-bold mt-0.5 ${attending ? "text-amber-300/90" : "text-white/45"}`}>
                      {header.weekday}
                    </div>
                  )}
                  {attending && <div className="text-[9px] text-amber-300/70 mt-0.5">מגיע</div>}
                </div>
                <div>
                  <div className="text-[9px] text-white/50 mb-0.5 text-right">מ־</div>
                  <Input
                    type="time"
                    value={draft[i]?.start || ""}
                    onChange={(e) => setField(i, "start", e.target.value)}
                    className="bg-stone-800 border-white/15 text-stone-100 h-8 text-sm text-center"
                    dir="ltr"
                    data-testid={`ff-crew-hours-start-${row?.id}-${i}`}
                  />
                </div>
                <div>
                  <div className="text-[9px] text-white/50 mb-0.5 text-right">עד</div>
                  <Input
                    type="time"
                    value={draft[i]?.end || ""}
                    onChange={(e) => setField(i, "end", e.target.value)}
                    className="bg-stone-800 border-white/15 text-stone-100 h-8 text-sm text-center"
                    dir="ltr"
                    data-testid={`ff-crew-hours-end-${row?.id}-${i}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, [i]: { start: "", end: "" } }))}
                  className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/25 rounded p-1 transition self-end"
                  title="נקה יום זה"
                  data-testid={`ff-crew-hours-clear-${row?.id}-${i}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10 mt-2">
          <Button onClick={() => onOpenChange(false)} variant="outline" className="bg-transparent border-white/20 text-stone-200 hover:bg-white/10" data-testid={`ff-crew-hours-cancel-${row?.id}`}>
            ביטול
          </Button>
          <Button onClick={handleConfirm} className="bg-amber-500 hover:bg-amber-400 text-black font-extrabold" data-testid={`ff-crew-hours-confirm-${row?.id}`}>
            אישור ושמירה
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// -----------------------------------------------------------------------
// WhatsAppSendDialog — owner spec 2026-07-08f
// Two-tab picker for sending a WhatsApp message to a single crew member:
//   Tab 1 · "אישית"   — free-text body persisted per production as a
//                        reusable template. Saving it here means the next
//                        crew member's dialog opens with the same body.
//   Tab 2 · "לו״ז"    — auto-assembled message listing that person's
//                        days + hours + production location.
// The final "שלח" button opens wa.me with the message prefilled.
// -----------------------------------------------------------------------
function WhatsAppSendDialog({ open, onOpenChange, row, prodName, production, dayHeaders, template, onSaveTemplate }) {
  const [tab, setTab] = useState("personal"); // "personal" | "schedule"
  const [personalBody, setPersonalBody] = useState("");
  const [scheduleBody, setScheduleBody] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);

  // Format a "HH:MM" pair as "8:00 ל־9:00", trimming zero-minutes only when
  // both sides are whole hours (keeps the SA-preferred natural phrasing).
  const fmtRange = (h) => {
    const s = (h?.start || "").trim();
    const e = (h?.end || "").trim();
    if (!s && !e) return "";
    return `${s || "?"} ל־${e || "?"}`;
  };

  useEffect(() => {
    if (!open) return;
    // ----- Personal tab -----
    const namePrefix = row?.name ? `${row.name}\n` : "";
    // If a saved template exists on the production, use it; otherwise
    // fall back to the SA's default first-contact text.
    const defaultBody =
      "שלום\nמה שלומך?\n\n" +
      "זה ניר דדון המפיק בפועל של הסדרה להתאהב ולהישאר בחיים\n" +
      "רציתי לשאול, יש לך העדפה באוכל לצמחוני | טבעוני | בשר\n" +
      "והאם יש כשרות מיוחד?";
    const body = (template && template.trim()) ? template : defaultBody;
    setPersonalBody(namePrefix + body);

    // ----- Schedule tab -----
    const lines = [];
    if (row?.name) lines.push(row.name);
    lines.push("שלום, להלן הימים והשעות שלך:");
    lines.push("");
    for (let i = 0; i < 5; i++) {
      if (!row?.days?.[i]) continue;
      const dh = dayHeaders?.[i] || {};
      const range = fmtRange(row?.hours?.[i] || row?.hours?.[String(i)]);
      const bits = [];
      if (dh.weekday) bits.push(dh.weekday);
      if (dh.date) bits.push(`בתאריך ${dh.date}`);
      const prefix = bits.join(" ") || `יום ${i + 1}`;
      // Owner spec 2026-07-08r — day + date + hours WRAPPED in WhatsApp
      // bold markers (`*…*`) so the crew member sees the schedule line
      // pop even in long threads.
      if (range) lines.push(`*${prefix} — בין השעות ${range}*`);
      else       lines.push(`*${prefix}*`);
      const perDayLoc =
        production?.location_by_day?.[i] ??
        production?.location_by_day?.[String(i)] ??
        "";
      const loc = perDayLoc || production?.location || "";
      if (loc) lines.push(`   כתובת: ${loc}`);
    }
    lines.push("");
    lines.push("יש להגיע עצמאית.");
    setScheduleBody(lines.join("\n"));
  }, [open, row?.id, template, production?.location]);

  const currentBody = tab === "personal" ? personalBody : scheduleBody;

  const handleSend = () => {
    if (!row?.phone) { toast.error("אין מספר טלפון"); return; }
    const link = waHref(row.phone, currentBody);
    if (!link) { toast.error("שגיאה ביצירת קישור WhatsApp"); return; }
    window.open(link, "_blank");
    onOpenChange(false);
  };

  const handleSaveTemplate = async () => {
    // Strip the leading `{name}\n` prefix before saving so the template
    // stays reusable for other crew members.
    const namePrefix = row?.name ? `${row.name}\n` : "";
    let bodyToSave = personalBody;
    if (namePrefix && bodyToSave.startsWith(namePrefix)) {
      bodyToSave = bodyToSave.slice(namePrefix.length);
    }
    setSavingTpl(true);
    try {
      await onSaveTemplate(bodyToSave);
    } finally {
      setSavingTpl(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-lg" data-testid={`ff-crew-wa-dialog-${row?.id}`}>
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-emerald-400" />
            <span className="bg-gradient-to-l from-emerald-300 to-teal-300 bg-clip-text text-transparent">
              WhatsApp — {row?.name || "איש צוות"}
            </span>
          </DialogTitle>
          <DialogDescription className="text-right text-stone-400 text-xs">
            בחר תבנית הודעה. &quot;שמור כתבנית&quot; שומר את ההודעה האישית עבור כל אנשי הצוות הבאים.
          </DialogDescription>
        </DialogHeader>

        {/* Tab switcher */}
        <div className="grid grid-cols-2 gap-1 bg-black/30 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setTab("personal")}
            className={`rounded-md py-2 text-sm font-bold transition ${tab === "personal" ? "bg-emerald-500 text-black" : "text-stone-300 hover:bg-white/5"}`}
            data-testid={`ff-crew-wa-tab-personal-${row?.id}`}
          >
            הודעה אישית
          </button>
          <button
            type="button"
            onClick={() => setTab("schedule")}
            className={`rounded-md py-2 text-sm font-bold transition ${tab === "schedule" ? "bg-emerald-500 text-black" : "text-stone-300 hover:bg-white/5"}`}
            data-testid={`ff-crew-wa-tab-schedule-${row?.id}`}
          >
            הודעה עם לו״ז
          </button>
        </div>

        {/* Body */}
        {tab === "personal" ? (
          <textarea
            value={personalBody}
            onChange={(e) => setPersonalBody(e.target.value)}
            rows={10}
            className="w-full bg-stone-800 border border-white/15 rounded-md p-3 text-sm leading-relaxed text-stone-100 resize-y"
            dir="rtl"
            data-testid={`ff-crew-wa-body-personal-${row?.id}`}
          />
        ) : (
          <textarea
            value={scheduleBody}
            onChange={(e) => setScheduleBody(e.target.value)}
            rows={10}
            className="w-full bg-stone-800 border border-white/15 rounded-md p-3 text-sm leading-relaxed text-stone-100 resize-y"
            dir="rtl"
            data-testid={`ff-crew-wa-body-schedule-${row?.id}`}
          />
        )}

        {/* Live hints */}
        {tab === "schedule" && !production?.location && (
          <div className="text-[11px] text-amber-300/90 bg-amber-950/30 border border-amber-400/30 rounded-md p-2">
            טיפ: עדיין לא הוזן לוקיישן להפקה — לחץ &quot;ערוך&quot; בכרטיס ההפקה והוסף כתובת מלאה כדי שהיא תופיע בהודעה.
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-2 border-t border-white/10 mt-2 flex-wrap">
          {tab === "personal" ? (
            <Button
              type="button"
              onClick={handleSaveTemplate}
              disabled={savingTpl}
              variant="outline"
              className="bg-transparent border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/15 font-bold gap-1.5"
              data-testid={`ff-crew-wa-save-template-${row?.id}`}
            >
              {savingTpl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              שמור כתבנית
            </Button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button onClick={() => onOpenChange(false)} variant="outline" className="bg-transparent border-white/20 text-stone-200 hover:bg-white/10" data-testid={`ff-crew-wa-cancel-${row?.id}`}>
              ביטול
            </Button>
            <Button
              onClick={handleSend}
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold gap-1.5"
              data-testid={`ff-crew-wa-send-${row?.id}`}
            >
              <Send className="h-4 w-4" />
              שלח בוואטסאפ
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// -----------------------------------------------------------------------
// ProductionToolbar — owner spec 2026-07-08h
// Compact toolbar rendered at the top of the crew dialog so the SA can
// edit / duplicate the production and update its location without leaving
// the dialog.
// -----------------------------------------------------------------------
function ProductionToolbar({ production, companyId, dayHeaders, onSaved, onClose }) {
  const navigate = useNavigate();
  const [location, setLocation] = useState("");
  const [locStatus, setLocStatus] = useState("idle"); // idle | saving | saved
  const [editOpen, setEditOpen] = useState(false);
  const [perDayOpen, setPerDayOpen] = useState(false);
  const [dupBusy, setDupBusy] = useState(false);
  const hydratedLoc = useRef(false);
  const lastSavedLocRef = useRef("");

  // Seed the location input once per production.
  useEffect(() => {
    if (!production?.id) return;
    setLocation(production.location || "");
    hydratedLoc.current = false;
    lastSavedLocRef.current = "";
  }, [production?.id, production?.location]);

  // Debounced auto-save of the location field.
  useEffect(() => {
    if (!production?.id) return;
    if (!hydratedLoc.current) {
      hydratedLoc.current = true;
      lastSavedLocRef.current = location;
      return;
    }
    if (location === lastSavedLocRef.current) return; // no real change
    const t = setTimeout(async () => {
      setLocStatus("saving");
      try {
        await updateProduction(
          production.id,
          { location },
          companyId
        );
        lastSavedLocRef.current = location;
        setLocStatus("saved");
        onSaved && onSaved();
      } catch (e) {
        setLocStatus("idle");
        toast.error(e.response?.data?.detail || "שמירת הכתובת נכשלה");
      }
    }, 700);
    return () => clearTimeout(t);
  }, [location, production, companyId, onSaved]);

  useEffect(() => {
    if (locStatus !== "saved") return;
    const t = setTimeout(() => setLocStatus("idle"), 600);
    return () => clearTimeout(t);
  }, [locStatus]);

  const handleDuplicate = async () => {
    if (!production?.id || dupBusy) return;
    if (!window.confirm(`לשכפל את כרטיס ההפקה "${production.name}"?`)) return;
    setDupBusy(true);
    try {
      const created = await duplicateProduction(production.id, companyId);
      toast.success("הכרטיסייה שוכפלה");
      onClose && onClose();
      if (created?.id) navigate(`/full-frame/productions/${created.id}`);
      else onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "השכפול נכשל");
    } finally {
      setDupBusy(false);
    }
  };

  return (
    <>
      <div className="rounded-xl border border-white/10 bg-stone-800/60 p-2 flex items-center gap-2 flex-wrap" data-testid="ff-crew-prod-toolbar">
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          className="bg-sky-500/25 hover:bg-sky-500 text-sky-200 hover:text-white transition rounded-lg px-3 py-1.5 flex items-center gap-1.5 font-bold text-xs border border-sky-400/40"
          data-testid="ff-crew-edit-prod-btn"
        >
          <Pencil className="h-4 w-4" />
          ערוך כרטיס
        </button>
        <button
          type="button"
          onClick={handleDuplicate}
          disabled={dupBusy}
          className="bg-indigo-500/25 hover:bg-indigo-500 text-indigo-200 hover:text-white transition rounded-lg px-3 py-1.5 flex items-center gap-1.5 font-bold text-xs border border-indigo-400/40 disabled:opacity-60"
          data-testid="ff-crew-dup-prod-btn"
        >
          {dupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
          שכפל כרטיס
        </button>
        <div className="flex-1 min-w-[220px] flex items-center gap-1.5 bg-black/25 rounded-lg border border-white/15 px-2 py-1">
          <MapPin className="h-4 w-4 text-amber-300 shrink-0" />
          <span className="text-[11px] font-bold text-stone-300 shrink-0 whitespace-nowrap">כתובת לוקיישן:</span>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="לדוגמה: רח' הרצל 12, תל אביב"
            className="flex-1 min-w-0 bg-transparent outline-none text-stone-100 text-sm placeholder:text-stone-500"
            data-testid="ff-crew-prod-location-input"
          />
          {locStatus === "saving" && <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400 shrink-0" />}
          {locStatus === "saved"  && <span className="text-emerald-300 text-xs font-bold whitespace-nowrap">✓ נשמר</span>}
          {/* Owner spec 2026-07-08j — open per-date locations popup. */}
          <button
            type="button"
            onClick={() => setPerDayOpen(true)}
            className="shrink-0 bg-amber-500/25 hover:bg-amber-500 text-amber-200 hover:text-black border border-amber-400/40 rounded-md px-2 py-0.5 text-[11px] font-extrabold whitespace-nowrap transition"
            title="לוקיישנים לפי תאריך"
            data-testid="ff-crew-prod-perday-btn"
          >
            🗓️ פר-תאריך
          </button>
        </div>
      </div>
      <QuickEditProductionDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        production={production}
        companyId={companyId}
        onSaved={onSaved}
      />
      <LocationsPerDayDialog
        open={perDayOpen}
        onOpenChange={setPerDayOpen}
        production={production}
        companyId={companyId}
        dayHeaders={dayHeaders}
        masterLocation={location}
        onSaved={onSaved}
      />
    </>
  );
}

// -----------------------------------------------------------------------
// QuickEditProductionDialog — mini edit form (name / client / contact /
// phone / location) accessible from inside the crew dialog.
// -----------------------------------------------------------------------
function QuickEditProductionDialog({ open, onOpenChange, production, companyId, onSaved }) {
  const [form, setForm] = useState({ name: "", client_name: "", contact_name: "", contact_phone: "", location: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !production) return;
    setForm({
      name: production.name || "",
      client_name: production.client_name || "",
      contact_name: production.contact_name || "",
      contact_phone: production.contact_phone || "",
      location: production.location || "",
    });
  }, [open, production]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) { toast.error("שם ההפקה חובה"); return; }
    setSaving(true);
    try {
      await updateProduction(production.id, form, companyId);
      toast.success("הכרטיס עודכן");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "שמירת הכרטיס נכשלה");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-md" data-testid="ff-crew-quick-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <Pencil className="h-5 w-5 text-sky-400" />
            עריכת כרטיס הפקה
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-2 pt-1">
          <label className="block text-xs font-bold text-stone-300">שם ההפקה</label>
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-stone-800 border-white/10 text-stone-100" data-testid="ff-crew-quick-edit-name" />
          <label className="block text-xs font-bold text-stone-300">שם החברה / לקוח</label>
          <Input value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} className="bg-stone-800 border-white/10 text-stone-100" data-testid="ff-crew-quick-edit-client" />
          <label className="block text-xs font-bold text-stone-300">איש קשר בלקוח</label>
          <Input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} className="bg-stone-800 border-white/10 text-stone-100" data-testid="ff-crew-quick-edit-contact" />
          <label className="block text-xs font-bold text-stone-300">טלפון איש הקשר</label>
          <Input value={form.contact_phone} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} className="bg-stone-800 border-white/10 text-stone-100 text-left" dir="ltr" data-testid="ff-crew-quick-edit-phone" />
          <label className="block text-xs font-bold text-stone-300">כתובת לוקיישן</label>
          <Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="לדוגמה: רח' הרצל 12, תל אביב" className="bg-stone-800 border-white/10 text-stone-100" data-testid="ff-crew-quick-edit-location" />
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="px-3 py-1.5 rounded-lg border border-white/15 bg-stone-800 hover:bg-stone-700 text-sm font-bold" data-testid="ff-crew-quick-edit-cancel">ביטול</button>
            <button type="submit" disabled={saving} className="px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-bold disabled:opacity-60 flex items-center gap-1.5" data-testid="ff-crew-quick-edit-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              שמור
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// -----------------------------------------------------------------------
// LocationsPerDayDialog — owner spec 2026-07-08j
// Popup listing the 5 production dates so the SA can override the master
// location per date. Empty rows fall back to the master location. Values
// are persisted under `production.location_by_day` as a { "0": string, … }
// map keyed by day index.
// -----------------------------------------------------------------------
function LocationsPerDayDialog({ open, onOpenChange, production, companyId, dayHeaders, masterLocation, onSaved }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const seed = {};
    const src = production?.location_by_day || {};
    for (let i = 0; i < 5; i++) {
      seed[i] = src[i] ?? src[String(i)] ?? "";
    }
    setDraft(seed);
  }, [open, production?.id]);

  const handleSave = async () => {
    const clean = {};
    for (let i = 0; i < 5; i++) {
      const v = (draft[i] || "").trim();
      if (v) clean[String(i)] = v;
    }
    // Owner spec 2026-07-08p — if the SA cleared everything, DON'T call
    // the endpoint with an empty {} — the backend now guards this too,
    // but making it explicit here avoids any race with concurrent saves.
    if (Object.keys(clean).length === 0) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      // Send ONLY the field being updated, not the whole production, so
      // no other field can accidentally be overwritten with stale data.
      await updateProduction(
        production.id,
        { location_by_day: clean },
        companyId
      );
      onSaved && onSaved();
      toast.success("הלוקיישנים לפי תאריך נשמרו");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "השמירה נכשלה");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-lg" data-testid="ff-crew-perday-loc-dialog">
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <MapPin className="h-5 w-5 text-amber-400" />
            <span className="bg-gradient-to-l from-amber-300 to-orange-300 bg-clip-text text-transparent">
              לוקיישנים לפי תאריך
            </span>
          </DialogTitle>
          <DialogDescription className="text-right text-stone-400 text-xs">
            שאר שדה ריק כדי להשתמש בכתובת הראשית של ההפקה. הלוקיישן שיוגדר כאן יופיע מתחת לתאריך ובהודעת ה-WhatsApp עם הלו״ז.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 pt-1">
          {[0, 1, 2, 3, 4].map((i) => {
            const header = dayHeaders?.[i] || { date: `יום ${i + 1}`, weekday: "" };
            const val = draft[i] || "";
            const usingFallback = !val && masterLocation;
            return (
              <div key={i} className="grid grid-cols-[80px,1fr] gap-2 items-center rounded-lg border border-white/10 bg-black/20 p-2" data-testid={`ff-crew-perday-row-${i}`}>
                <div className="text-center leading-tight">
                  <div className="text-[13px] font-extrabold text-amber-200">{header.date}</div>
                  {header.weekday && (
                    <div className="text-[10px] font-bold text-amber-300/80 mt-0.5">{header.weekday}</div>
                  )}
                </div>
                <div>
                  <input
                    type="text"
                    value={val}
                    onChange={(e) => setDraft((prev) => ({ ...prev, [i]: e.target.value }))}
                    placeholder={masterLocation ? `ברירת מחדל: ${masterLocation}` : "כתובת מלאה"}
                    className="w-full bg-stone-800 border border-white/15 rounded-md px-2 py-1.5 text-sm text-stone-100 placeholder:text-stone-500 placeholder:text-xs"
                    data-testid={`ff-crew-perday-input-${i}`}
                  />
                  {usingFallback && (
                    <div className="text-[10px] text-stone-500 mt-0.5">משתמש בכתובת הראשית</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10 mt-2">
          <Button onClick={() => onOpenChange(false)} variant="outline" className="bg-transparent border-white/20 text-stone-200 hover:bg-white/10" data-testid="ff-crew-perday-cancel">
            ביטול
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-black font-extrabold gap-1.5" data-testid="ff-crew-perday-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            שמור
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------
// CateringSlots — owner spec 2026-07-08m
// Three compact slots (breakfast / lunch / dinner) rendered above every
// day summary cube. Each slot: emoji + name input + phone input + WA
// button. Values are patched up via `onPatch(dayIdx, slot, field, val)`
// and the parent debounces the actual save.
// -----------------------------------------------------------------------
const CATERING_SLOTS = [
  { key: "morning",  emoji: "☀️", label: "בוקר" },
  { key: "noon",     emoji: "🍽️", label: "צהריים" },
  { key: "evening",  emoji: "🌙", label: "ערב" },
];
function CateringSlots({ dayIdx, catering, onPatch }) {
  return (
    <div className="space-y-1 mb-2" data-testid={`ff-crew-catering-${dayIdx}`}>
      {CATERING_SLOTS.map((s) => {
        const val = catering[s.key] || {};
        const wa = waHref(val.phone || "", val.name ? `שלום ${val.name}, לגבי הקיטרינג...` : "");
        return (
          <div key={s.key} className="bg-black/35 rounded-md border border-amber-400/25 p-1 space-y-0.5">
            <div className="text-[9px] font-extrabold text-amber-200 flex items-center justify-center gap-1">
              <span>{s.emoji}</span>
              <span>{s.label}</span>
            </div>
            <input
              type="text"
              value={val.name || ""}
              onChange={(e) => onPatch(dayIdx, s.key, "name", e.target.value)}
              placeholder="שם"
              className="w-full bg-stone-900/70 border border-white/10 rounded px-1 py-0.5 text-[10px] text-stone-100 placeholder:text-stone-500 text-center outline-none focus:border-amber-400/60"
              data-testid={`ff-crew-catering-name-${dayIdx}-${s.key}`}
            />
            <div className="flex items-center gap-1">
              <input
                type="tel"
                value={val.phone || ""}
                onChange={(e) => onPatch(dayIdx, s.key, "phone", e.target.value)}
                placeholder="טלפון"
                className="flex-1 min-w-0 bg-stone-900/70 border border-white/10 rounded px-1 py-0.5 text-[10px] text-stone-100 placeholder:text-stone-500 text-center outline-none focus:border-amber-400/60"
                dir="ltr"
                data-testid={`ff-crew-catering-phone-${dayIdx}-${s.key}`}
              />
              <a
                href={wa || undefined}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => { if (!wa) e.preventDefault(); }}
                className={`shrink-0 rounded flex items-center justify-center transition h-5 w-5 ${wa ? "bg-emerald-600/80 hover:bg-emerald-500 text-white" : "bg-stone-800 text-stone-600 cursor-not-allowed"}`}
                title="WhatsApp לספק הקיטרינג"
                data-testid={`ff-crew-catering-wa-${dayIdx}-${s.key}`}
              >
                <MessageCircle className="h-3 w-3" />
              </a>
            </div>
          </div>
        );
      })}
    </div>
  );
}


// -----------------------------------------------------------------------
// CrewDocumentsDialog — owner spec 2026-07-08t
// Popup per crew row: attach הצעת מחיר and/or חשבונית, pick a recipient
// from the whole crew, add notes, and blast the doc URLs via WhatsApp.
// Files are stored server-side; the message includes the URLs so the
// receiver taps them to view/download.
// -----------------------------------------------------------------------
function CrewDocumentsDialog({ open, onOpenChange, row, production, companyId, allRows }) {
  const [quoteBusy, setQuoteBusy]     = useState(false);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [quoteDoc,   setQuoteDoc]   = useState(null);
  const [invoiceDoc, setInvoiceDoc] = useState(null);
  const [notes, setNotes] = useState("");
  // Owner spec 2026-07-08u — recipient chosen PER document line (not
  // globally), so the SA can e.g. send the quote to one person and the
  // invoice to another. Default to the row's owner.
  const [quoteRecipientId,   setQuoteRecipientId]   = useState("");
  const [invoiceRecipientId, setInvoiceRecipientId] = useState("");
  // Owner spec 2026-07-08v — recipient for the notes-only send button.
  const [notesRecipientId,   setNotesRecipientId]   = useState("");

  // Recipient list — everyone in the production with a phone number.
  const recipients = useMemo(() => {
    return (allRows || [])
      .filter((r) => r.phone && (r.name || r.role))
      .map((r) => ({ id: r.id, label: `${r.name || r.role} · ${r.phone}`, phone: r.phone, name: r.name || r.role }));
  }, [allRows]);

  useEffect(() => {
    if (!open) return;
    // Owner spec 2026-07-08v — LOAD the last uploaded quote/invoice
    // (if any) from the production doc so the popup reopens with the
    // files already attached. This is the "save" the SA expected.
    const docs = production?.crew_documents || [];
    const lastByType = (t) => {
      const filtered = docs.filter((d) => d.type === t);
      if (filtered.length === 0) return null;
      // pick the most recent by uploaded_at (or last in the array).
      return filtered[filtered.length - 1];
    };
    setQuoteDoc(lastByType("quote"));
    setInvoiceDoc(lastByType("invoice"));
    setNotes("");
    setQuoteRecipientId(row?.id || "");
    setInvoiceRecipientId(row?.id || "");
    setNotesRecipientId(row?.id || "");
  }, [open, row?.id, production?.crew_documents]);

  const publicUrl = (relUrl) => {
    if (!relUrl) return "";
    if (/^https?:/i.test(relUrl)) return relUrl;
    // Storage URLs are always served by the backend, NOT the frontend
    // origin. Use REACT_APP_BACKEND_URL so preview + prod both work.
    return `${BACKEND_URL}${relUrl}`;
  };

  // Owner spec 2026-07-08w — internal document viewer. When a file is
  // uploaded, clicking the filename opens THIS modal (not a new tab) with
  // an inline preview (iframe for PDF, <img> for images).
  const [viewerDoc, setViewerDoc] = useState(null); // {url, filename} or null

  const handleUpload = async (docType, file) => {
    if (!file) return;
    const setBusy = docType === "quote" ? setQuoteBusy : setInvoiceBusy;
    const setDoc  = docType === "quote" ? setQuoteDoc  : setInvoiceDoc;
    setBusy(true);
    try {
      const entry = await uploadCrewDocument(production.id, { file, docType, notes: "" }, companyId);
      setDoc(entry);
      toast.success(docType === "quote" ? "הצעת המחיר הועלתה" : "החשבונית הועלתה");
    } catch (e) {
      toast.error(e.response?.data?.detail || "ההעלאה נכשלה");
    } finally {
      setBusy(false);
    }
  };

  const sendDoc = (doc, docLabel, recipient) => {
    if (!doc)              { toast.error("העלה קודם את הקובץ"); return; }
    if (!recipient?.phone) { toast.error("בחר מקבל תקין"); return; }
    const lines = [];
    lines.push(`שלום ${recipient.name},`);
    if (notes.trim()) { lines.push(""); lines.push(notes.trim()); }
    lines.push("");
    lines.push(`*${docLabel}:*`);
    lines.push(publicUrl(doc.url));
    const link = waHref(recipient.phone, lines.join("\n"));
    if (!link) { toast.error("שגיאה ביצירת קישור WhatsApp"); return; }
    window.open(link, "_blank");
  };

  const quoteRecipient   = recipients.find((r) => r.id === quoteRecipientId);
  const invoiceRecipient = recipients.find((r) => r.id === invoiceRecipientId);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-lg" data-testid={`ff-crew-docs-dialog-${row?.id}`}>
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <Paperclip className="h-5 w-5 text-amber-400" />
            <span className="bg-gradient-to-l from-amber-300 to-orange-300 bg-clip-text text-transparent">
              שליחת מסמכים
            </span>
          </DialogTitle>
          <DialogDescription className="text-right text-stone-400 text-xs">
            בכל שורה: העלה קובץ, בחר לאיזה איש צוות לשלוח, ולחץ &quot;שלח&quot;.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          {/* ------- Quote line ------- */}
          <DocumentLine
            title="1. הצעת מחיר"
            titleColor="text-amber-200"
            btnBg="bg-amber-500/30 hover:bg-amber-500/50 border-amber-400/40"
            rowId={row?.id}
            docType="quote"
            doc={quoteDoc}
            busy={quoteBusy}
            recipients={recipients}
            recipientId={quoteRecipientId}
            onRecipient={setQuoteRecipientId}
            onUpload={(f) => handleUpload("quote", f)}
            onSend={() => sendDoc(quoteDoc, "הצעת מחיר", quoteRecipient)}
            onView={() => quoteDoc && setViewerDoc({ url: publicUrl(quoteDoc.url), filename: quoteDoc.filename })}
            publicUrl={publicUrl}
          />

          {/* ------- Invoice line ------- */}
          <DocumentLine
            title="2. חשבונית"
            titleColor="text-sky-200"
            btnBg="bg-sky-500/30 hover:bg-sky-500/50 border-sky-400/40"
            rowId={row?.id}
            docType="invoice"
            doc={invoiceDoc}
            busy={invoiceBusy}
            recipients={recipients}
            recipientId={invoiceRecipientId}
            onRecipient={setInvoiceRecipientId}
            onUpload={(f) => handleUpload("invoice", f)}
            onSend={() => sendDoc(invoiceDoc, "חשבונית", invoiceRecipient)}
            onView={() => invoiceDoc && setViewerDoc({ url: publicUrl(invoiceDoc.url), filename: invoiceDoc.filename })}
            publicUrl={publicUrl}
          />

          {/* Notes (shared + can also be sent on its own) */}
          <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2">
            <div className="font-extrabold text-emerald-200 text-sm">3. הערות</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={6}
              placeholder="כתוב כאן את ההודעה — יופיעה בכל שליחה מהחלון הזה, וגם אפשר לשלוח את הטקסט לבד"
              className="w-full bg-stone-800 border border-white/15 rounded-md p-3 text-sm text-stone-100 resize-y placeholder:text-stone-500 leading-relaxed"
              data-testid={`ff-crew-doc-notes-${row?.id}`}
            />
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={notesRecipientId}
                onChange={(e) => setNotesRecipientId(e.target.value)}
                className="flex-1 min-w-[140px] bg-stone-800 border border-white/15 rounded-md px-2 py-1.5 text-xs text-stone-100 outline-none focus:border-emerald-400/60"
                data-testid={`ff-crew-doc-notes-recipient-${row?.id}`}
              >
                <option value="">— בחר מקבל —</option>
                {recipients.map((r) => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => {
                  const rec = recipients.find((r) => r.id === notesRecipientId);
                  if (!rec?.phone)  { toast.error("בחר מקבל תקין"); return; }
                  if (!notes.trim()){ toast.error("כתוב הערה קודם"); return; }
                  const msg = `שלום ${rec.name},\n\n${notes.trim()}`;
                  const link = waHref(rec.phone, msg);
                  if (!link) { toast.error("שגיאה ביצירת קישור"); return; }
                  window.open(link, "_blank");
                }}
                disabled={!notes.trim() || !notesRecipientId}
                className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:hover:bg-emerald-500 text-black font-extrabold text-xs rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 transition"
                data-testid={`ff-crew-doc-notes-send-${row?.id}`}
                title="שלח את ההערות לבד בוואטסאפ"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                שלח הערות
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10 mt-2">
          <Button onClick={() => onOpenChange(false)} variant="outline" className="bg-transparent border-white/20 text-stone-200 hover:bg-white/10" data-testid={`ff-crew-doc-cancel-${row?.id}`}>
            סגור
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Internal file viewer — iframe for PDF, <img> for images. Opens
        inside the app; no new tab, no download. */}
    <DocumentViewerDialog
      open={!!viewerDoc}
      onOpenChange={(v) => { if (!v) setViewerDoc(null); }}
      doc={viewerDoc}
    />
    </>
  );
}

// -----------------------------------------------------------------------
// DocumentViewerDialog — internal in-app preview for uploaded quotes /
// invoices. Detects PDF vs image by URL extension and renders inline
// so the SA never leaves the system.
// -----------------------------------------------------------------------
function DocumentViewerDialog({ open, onOpenChange, doc }) {
  const url = doc?.url || "";
  const isPdf   = /\.pdf(\?|$)/i.test(url);
  const isImage = /\.(png|jpe?g|gif|webp|bmp|heic|heif)(\?|$)/i.test(url);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        dir="rtl"
        className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-5xl w-[95vw] max-h-[95vh] p-0 overflow-hidden"
        data-testid="ff-crew-doc-viewer"
      >
        <DialogHeader className="px-4 py-3 border-b border-white/10">
          <DialogTitle className="text-right flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-amber-400" />
            <span className="truncate">{doc?.filename || "תצוגה מקדימה"}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="bg-stone-950 flex items-center justify-center overflow-auto" style={{ height: "80vh" }}>
          {!url ? (
            <div className="text-stone-500 text-sm p-8">אין קובץ להצגה</div>
          ) : isImage ? (
            <img
              src={url}
              alt={doc?.filename || "document"}
              className="max-w-full max-h-full object-contain"
              data-testid="ff-crew-doc-viewer-img"
            />
          ) : isPdf ? (
            <iframe
              src={url}
              title={doc?.filename || "document"}
              className="w-full h-full border-0 bg-white"
              data-testid="ff-crew-doc-viewer-iframe"
            />
          ) : (
            <iframe
              src={url}
              title={doc?.filename || "document"}
              className="w-full h-full border-0 bg-white"
              data-testid="ff-crew-doc-viewer-iframe"
            />
          )}
        </div>
        <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-white/10">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-stone-400 hover:text-stone-200 underline underline-offset-2"
            data-testid="ff-crew-doc-viewer-open-external"
          >
            פתח בכרטיסייה נפרדת
          </a>
          <Button
            onClick={() => onOpenChange(false)}
            variant="outline"
            className="bg-transparent border-white/20 text-stone-200 hover:bg-white/10"
            data-testid="ff-crew-doc-viewer-close"
          >
            סגור
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Single document row: title • [file button] • [recipient picker] • [WA send]
function DocumentLine({ title, titleColor, btnBg, rowId, docType, doc, busy, recipients, recipientId, onRecipient, onUpload, onSend, onView, publicUrl }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className={`font-extrabold text-sm ${titleColor}`}>{title}</div>
        {doc && (
          <button
            type="button"
            onClick={onView}
            className="text-emerald-300 text-xs font-bold hover:text-emerald-200 underline underline-offset-2 truncate max-w-[220px] text-right"
            data-testid={`ff-crew-doc-${docType}-view-${rowId}`}
            title="לחץ לצפייה פנימית בקובץ"
          >
            ✓ {doc.filename}
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className={`cursor-pointer inline-flex items-center gap-1.5 border rounded-lg px-3 py-1.5 text-xs font-bold ${btnBg}`} data-testid={`ff-crew-doc-${docType}-upload-${rowId}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
          {doc ? "החלף קובץ" : "בחר קובץ"}
          <input
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            disabled={busy}
            onChange={(e) => onUpload(e.target.files?.[0])}
          />
        </label>
        <select
          value={recipientId}
          onChange={(e) => onRecipient(e.target.value)}
          className="flex-1 min-w-[140px] bg-stone-800 border border-white/15 rounded-md px-2 py-1.5 text-xs text-stone-100 outline-none focus:border-emerald-400/60"
          data-testid={`ff-crew-doc-${docType}-recipient-${rowId}`}
        >
          <option value="">— בחר מקבל —</option>
          {recipients.map((r) => (
            <option key={r.id} value={r.id}>{r.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={onSend}
          disabled={!doc || !recipientId}
          className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:hover:bg-emerald-500 text-black font-extrabold text-xs rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 transition"
          data-testid={`ff-crew-doc-${docType}-send-${rowId}`}
          title="שלח בוואטסאפ"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          שלח
        </button>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------
// TaxiOrderDialog — owner spec 2026-02-07
// Book a taxi for a crew member. Two WhatsApp send buttons:
//   1) to the CREW MEMBER (heads-up their taxi is coming)
//   2) to the TAXI COMPANY (the actual booking)
// All fields are free-text and ephemeral — nothing is persisted server-side,
// per owner request "אני אזין ידנית בכל פעם".
// -----------------------------------------------------------------------
function TaxiOrderDialog({ open, onOpenChange, row, production }) {
  const defaultDropoff = production?.location || Object.values(production?.location_by_day || {})[0] || "";
  // Owner spec 2026-02-07 — auto-detect taxi vendor(s) from production
  // expenses whose `role` contains "מונית" / "מוניות". The first match
  // pre-fills company + phone; the SA can still delete & override.
  const taxiVendors = useMemo(() => {
    const out = [];
    for (const e of (production?.expenses || [])) {
      const role = String(e?.role || "").trim();
      if (!role) continue;
      if (role.includes("מונית") || role.includes("מוניות")) {
        out.push({
          id: e.id,
          company: (e.provider || "").trim(),
          phone: (e.provider_phone || e.provider_whatsapp || "").trim(),
        });
      }
    }
    return out;
  }, [production?.expenses]);
  const defaultVendor = taxiVendors[0] || { company: "", phone: "" };

  // Owner spec 2026-02-07e — local mirror of saved cards. We mutate
  // THIS via setState so React re-renders reliably. The parent prop
  // (`production.taxi_saved_cards`) is also kept in sync so that if
  // the dialog is closed & re-opened, the freshest data is picked up
  // without another network round-trip.
  const [savedCardsLocal, setSavedCardsLocal] = useState(() => {
    const list = production?.taxi_saved_cards;
    return Array.isArray(list) ? list : [];
  });

  // Re-seed local state whenever the dialog is (re)opened OR the
  // parent-provided list changes size (e.g., another dialog on the
  // same production saved a card).
  useEffect(() => {
    if (!open) return;
    const list = production?.taxi_saved_cards;
    setSavedCardsLocal(Array.isArray(list) ? [...list] : []);
  }, [open, production?.taxi_saved_cards?.length]);

  const savedCards = useMemo(() => {
    if (savedCardsLocal.length > 0) {
      return [...savedCardsLocal].sort((a, b) => String(b.saved_at || "").localeCompare(String(a.saved_at || "")));
    }
    const legacy = production?.taxi_defaults;
    if (legacy && (legacy.taxi_company || legacy.taxi_phone || legacy.dropoff)) {
      return [{ id: "legacy", saved_at: "", ...legacy }];
    }
    return [];
  }, [savedCardsLocal, production?.taxi_defaults]);

  const latestSaved = savedCards[0] || {};
  const initialCompany = latestSaved.taxi_company || defaultVendor.company;
  const initialPhone   = latestSaved.taxi_phone   || defaultVendor.phone;
  const initialDropoff = latestSaved.dropoff      || defaultDropoff;
  const initialPickup  = row?.taxi_pickup || "";

  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [taxiCompany, setTaxiCompany] = useState(initialCompany);
  const [taxiPhone, setTaxiPhone] = useState(initialPhone);
  const [pickup, setPickup] = useState(initialPickup);
  const [dropoff, setDropoff] = useState(initialDropoff);
  const [pax, setPax] = useState(1);
  const [notes, setNotes] = useState("");
  const [savingKind, setSavingKind] = useState(null); // 'crew' | 'taxi' | null
  const [savingCard, setSavingCard] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);       // {kind, message, phone} | null

  // Owner spec 2026-02-07e — reset the form ONLY on open transition
  // false→true. If we included initialCompany/etc in deps, saving a
  // card (which changes latestSaved → initialCompany) would silently
  // wipe the user's inputs and collapse the just-opened saved panel.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!open || wasOpen) return;
    setDate("");
    setTime("");
    setTaxiCompany(initialCompany);
    setTaxiPhone(initialPhone);
    setPickup(initialPickup);
    setDropoff(initialDropoff);
    setPax(1);
    setNotes("");
    setConfirm(null);
    setSelectedDayIdx(null);
    setHistoryOpen(false);
    setSavedOpen(false);
     
  }, [open]);

  const formatDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  // Human-friendly summary of the crew member's shoot days. Since the
  // production doesn't carry per-day calendar dates in this build, we
  // just number them (יום 1..N) and pull the location + hours per day.
  const daySummary = useMemo(() => {
    const days = row?.days || [];
    const hours = row?.hours || {};
    const locByDay = production?.location_by_day || {};
    const items = [];
    for (let i = 0; i < days.length; i++) {
      if (!days[i]) continue;
      const h = hours[String(i)];
      const timeLabel = h && (h.start || h.end)
        ? `${h.start || "--:--"}${h.end ? `–${h.end}` : ""}`
        : "";
      items.push({
        index: i,
        label: `יום ${i + 1}`,
        time: timeLabel,
        start: (h && h.start) || "",
        location: (locByDay[String(i)] || "").trim(),
      });
    }
    return items;
  }, [row?.days, row?.hours, production?.location_by_day]);

  // Owner spec 2026-02-07d — clicking a day cube auto-fills the dropoff
  // (production location for THAT day) and the pickup time (day start hour).
  // We do NOT auto-fill `date` because this build has no per-day
  // calendar dates on the production doc.
  const [selectedDayIdx, setSelectedDayIdx] = useState(null);
  const applyDay = (d) => {
    setSelectedDayIdx(d.index);
    if (d.location) setDropoff(d.location);
    if (d.start)    setTime(d.start);
  };

  // Owner spec 2026-02-07d — clickable saved-orders history. The badge
  // toggles a list showing past taxi orders; each row has a "טען שוב"
  // button that re-populates the form from that order for reuse.
  const [historyOpen, setHistoryOpen] = useState(false);
  const loadFromHistory = (o) => {
    if (o.date)         setDate(o.date);
    if (o.time)         setTime(o.time);
    if (o.taxi_company) setTaxiCompany(o.taxi_company);
    if (o.taxi_phone)   setTaxiPhone(o.taxi_phone);
    if (o.pickup)       setPickup(o.pickup);
    if (o.dropoff)      setDropoff(o.dropoff);
    if (o.pax)          setPax(o.pax);
    if (o.notes)        setNotes(o.notes);
    setHistoryOpen(false);
    toast.success("הפרטים נטענו מההזמנה הקודמת");
  };

  const buildCrewMessage = () => {
    const lines = [];
    lines.push(`שלום ${row?.name || row?.role || ""},`);
    lines.push("");
    lines.push(`הזמנו לך מונית ליום *${formatDate(date)}* בשעה *${time}*.`);
    if (taxiCompany) lines.push(`חברת המוניות: *${taxiCompany}*${taxiPhone ? ` — ${taxiPhone}` : ""}`);
    if (pickup)  lines.push(`איסוף מ: ${pickup}`);
    if (dropoff) lines.push(`יעד: ${dropoff}`);
    if (notes.trim()) { lines.push(""); lines.push(notes.trim()); }
    lines.push("");
    lines.push("בהצלחה!");
    return lines.join("\n");
  };

  const buildTaxiMessage = () => {
    const lines = [];
    lines.push("שלום,");
    lines.push("");
    lines.push(`הזמנת מונית${production?.name ? ` להפקה: *${production.name}*` : ""}:`);
    lines.push(`נוסע: *${row?.name || row?.role || ""}* — ${row?.phone || ""}`);
    lines.push(`יום: *${formatDate(date)}*`);
    lines.push(`שעה: *${time}*`);
    if (pickup)  lines.push(`איסוף: ${pickup}`);
    if (dropoff) lines.push(`יעד: ${dropoff}`);
    if (pax > 1) lines.push(`מספר נוסעים: ${pax}`);
    if (notes.trim()) { lines.push(""); lines.push(notes.trim()); }
    lines.push("");
    lines.push("תודה!");
    return lines.join("\n");
  };

  const validateCore = () => {
    if (!date) { toast.error("בחר תאריך"); return false; }
    if (!time) { toast.error("בחר שעה"); return false; }
    return true;
  };

  // Owner spec 2026-02-07 — every send goes through a confirmation
  // dialog. On confirm we POST to the backend so the blue-dot indicator
  // sticks, then open wa.me in a new tab.
  const requestSend = (kind) => {
    if (!validateCore()) return;
    if (kind === "crew" && !row?.phone) { toast.error("אין מספר טלפון לאיש הצוות"); return; }
    if (kind === "taxi" && !taxiPhone.trim()) { toast.error("הזן טלפון של חברת המוניות"); return; }
    const message = kind === "crew" ? buildCrewMessage() : buildTaxiMessage();
    const phone = kind === "crew" ? row.phone : taxiPhone;
    const title = kind === "crew" ? "לאיש הצוות" : "לחברת המוניות";
    setConfirm({ kind, message, phone, title });
  };

  const performSend = async () => {
    if (!confirm) return;
    const { kind, phone, message } = confirm;
    setSavingKind(kind);
    try {
      // Log the order server-side FIRST so the blue-dot persists even
      // if wa.me opening is blocked by the browser.
      await addCrewTaxiOrder(production.id, row.id, {
        date, time,
        taxi_company: taxiCompany,
        taxi_phone: taxiPhone,
        pickup, dropoff,
        pax,
        notes,
        target: kind,
      }, production.company_id);
      const link = waHref(phone, message);
      if (!link) { toast.error("שגיאה ביצירת קישור WhatsApp"); return; }
      window.open(link, "_blank");
      toast.success(kind === "crew" ? "הזמנה נשלחה לאיש הצוות" : "הזמנה נשלחה לחברת המוניות");
      setConfirm(null);
      onOpenChange(false);
    } catch (err) {
      toast.error("שמירת ההזמנה נכשלה");
    } finally {
      setSavingKind(null);
    }
  };

  const saveCardDefaults = async () => {
    setSavingCard(true);
    try {
      const res = await saveCrewTaxiDefaults(production.id, row.id, {
        taxi_company: taxiCompany,
        taxi_phone: taxiPhone,
        dropoff,
        pickup,
      }, production.company_id);
      if (res?.card) {
        setSavedCardsLocal((prev) => [...prev, res.card]);
        // Keep the parent prop in sync so a next open (without a full
        // refetch) still sees the fresh card.
        if (production) {
          production.taxi_saved_cards = [...(production.taxi_saved_cards || []), res.card];
        }
        if (row) row.taxi_pickup = pickup;
      }
      toast.success("הכרטיסייה נשמרה. תופיע ברשימה למטה בכל פתיחה.");
      setSavedOpen(true);
    } catch (err) {
      toast.error("שמירה נכשלה");
    } finally {
      setSavingCard(false);
    }
  };

  const loadFromSavedCard = (card) => {
    if (card.taxi_company !== undefined) setTaxiCompany(card.taxi_company || "");
    if (card.taxi_phone   !== undefined) setTaxiPhone(card.taxi_phone   || "");
    if (card.dropoff      !== undefined) setDropoff(card.dropoff        || "");
    // pickup is per-row — only overwrite if the saved card has it
    if (card.pickup) setPickup(card.pickup);
    toast.success("הפרטים נטענו מהכרטיסייה השמורה");
  };

  const removeSavedCard = async (cardId) => {
    if (cardId === "legacy") { toast.error("כרטיסייה ישנה — לא ניתן למחוק"); return; }
    try {
      await deleteTaxiSavedCard(production.id, cardId, production.company_id);
      setSavedCardsLocal((prev) => prev.filter((c) => c.id !== cardId));
      if (production?.taxi_saved_cards) {
        production.taxi_saved_cards = production.taxi_saved_cards.filter((c) => c.id !== cardId);
      }
      toast.success("הכרטיסייה נמחקה");
    } catch {
      toast.error("מחיקה נכשלה");
    }
  };

  const inputCls = "w-full bg-stone-800 border border-white/15 rounded-md px-3 py-2 text-sm text-stone-100 outline-none focus:border-yellow-400/60 placeholder:text-stone-500";
  const labelCls = "block text-xs font-bold text-stone-300 mb-1";

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid={`ff-crew-taxi-dialog-${row?.id}`}>
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <Car className="h-5 w-5 text-yellow-400" />
            <span className="bg-gradient-to-l from-yellow-300 to-amber-300 bg-clip-text text-transparent">
              הזמנת מונית
            </span>
          </DialogTitle>
          <DialogDescription className="text-right text-stone-400 text-xs">
            מלא את הפרטים ושלח בוואטסאפ — לאיש הצוות ו/או לחברת המוניות.
          </DialogDescription>
        </DialogHeader>

        {/* Owner spec 2026-02-07 — crew member details card at the top so
            the SA sees name/role/phone/shoot-days/hours at a glance. */}
        <div
          className="rounded-lg border border-sky-500/30 bg-sky-500/10 p-3 space-y-2"
          data-testid={`ff-taxi-crew-card-${row?.id}`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sky-200 text-xs font-bold">איש צוות</div>
              <div className="text-white text-base font-extrabold truncate" data-testid={`ff-taxi-crew-name-${row?.id}`}>
                {row?.name || "—"}
              </div>
              <div className="text-stone-300 text-xs truncate">{row?.role || ""}</div>
            </div>
            {row?.phone && (
              <div className="text-emerald-300 text-sm font-bold" dir="ltr" data-testid={`ff-taxi-crew-phone-${row?.id}`}>
                {row.phone}
              </div>
            )}
          </div>
          {daySummary.length > 0 && (
            <div className="pt-2 border-t border-white/10">
              <div className="text-xs font-bold text-stone-300 mb-1">
                ימי צילום ושעות
                <span className="text-stone-500 font-normal mr-1.5">— לחץ על יום למילוי אוטומטי</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {daySummary.map((d) => {
                  const isSelected = selectedDayIdx === d.index;
                  return (
                    <button
                      key={d.index}
                      type="button"
                      onClick={() => applyDay(d)}
                      className={`rounded px-2 py-1 text-xs transition border ${
                        isSelected
                          ? "bg-sky-500 border-sky-300 text-black font-extrabold"
                          : "bg-white/5 border-white/10 hover:bg-sky-500/30 hover:border-sky-400/60"
                      }`}
                      data-testid={`ff-taxi-day-cube-${row?.id}-${d.index}`}
                      title={d.location ? `לוקיישן: ${d.location}` : "אין לוקיישן ליום זה"}
                    >
                      <span className="font-bold">{d.label}</span>
                      {d.time && <span className={`mr-1.5 ${isSelected ? "text-stone-800" : "text-sky-300"}`} dir="ltr">{d.time}</span>}
                      {d.location && <span className={`mr-1.5 ${isSelected ? "text-stone-800" : "text-emerald-300"}`}>· {d.location}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {(row?.taxi_orders?.length || 0) > 0 && (
            <div className="pt-2 border-t border-white/10">
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                className="w-full text-right text-sky-300 text-xs font-bold hover:text-sky-200 flex items-center justify-between"
                data-testid={`ff-taxi-history-toggle-${row?.id}`}
                title="לחץ להצגת הזמנות קודמות"
              >
                <span>נשלחו כבר {row.taxi_orders.length} הזמנות מונית — לחץ להצגה</span>
                <span className={`transition-transform ${historyOpen ? "rotate-180" : ""}`}>▼</span>
              </button>
              {historyOpen && (
                <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto" data-testid={`ff-taxi-history-list-${row?.id}`}>
                  {[...row.taxi_orders].reverse().map((o, idx) => (
                    <div
                      key={o.id || idx}
                      className="bg-black/30 border border-white/10 rounded px-2 py-1.5 flex items-center justify-between gap-2"
                    >
                      <div className="min-w-0 text-xs">
                        <div className="text-white font-bold truncate">
                          {o.date ? formatDate(o.date) : "—"}{o.time ? ` · ${o.time}` : ""}
                          {o.taxi_company ? ` · ${o.taxi_company}` : ""}
                        </div>
                        <div className="text-stone-400 truncate">
                          {o.target === "crew" ? "→ נשלח לאיש הצוות" : o.target === "taxi" ? "→ נשלח לחברת המוניות" : "→ נשלח"}
                          {o.pickup ? ` · מ: ${o.pickup}` : ""}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => loadFromHistory(o)}
                        className="shrink-0 bg-sky-500 hover:bg-sky-400 text-black font-bold text-xs rounded px-2 py-1 transition"
                        data-testid={`ff-taxi-history-reload-${row?.id}-${idx}`}
                        title="טען את הפרטים לטופס"
                      >
                        טען שוב
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {savedCards.length > 0 && (
            <div className="pt-2 border-t border-white/10">
              <button
                type="button"
                onClick={() => setSavedOpen((v) => !v)}
                className="w-full text-right text-emerald-300 text-xs font-bold hover:text-emerald-200 flex items-center justify-between"
                data-testid={`ff-taxi-saved-toggle-${row?.id}`}
                title="לחץ להצגת כרטיסיות שמורות"
              >
                <span>{savedCards.length} כרטיסיות שמורות — לחץ להצגה</span>
                <span className={`transition-transform ${savedOpen ? "rotate-180" : ""}`}>▼</span>
              </button>
              {savedOpen && (
                <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto" data-testid={`ff-taxi-saved-list-${row?.id}`}>
                  {savedCards.map((c, idx) => {
                    // saved_at ISO → nice Hebrew short: 07/02 14:30
                    const stamp = c.saved_at ? new Date(c.saved_at) : null;
                    const label = stamp && !isNaN(stamp.getTime())
                      ? `${String(stamp.getDate()).padStart(2,"0")}/${String(stamp.getMonth()+1).padStart(2,"0")} ${String(stamp.getHours()).padStart(2,"0")}:${String(stamp.getMinutes()).padStart(2,"0")}`
                      : "—";
                    return (
                      <div
                        key={c.id || idx}
                        className="bg-black/30 border border-emerald-500/20 rounded px-2 py-1.5 flex items-center justify-between gap-2"
                      >
                        <div className="min-w-0 text-xs">
                          <div className="text-white font-bold truncate">
                            {c.taxi_company || "ללא שם חברה"}
                            {c.taxi_phone ? ` · ${c.taxi_phone}` : ""}
                          </div>
                          <div className="text-stone-400 truncate">
                            נשמר: {label}
                            {c.dropoff ? ` · יעד: ${c.dropoff}` : ""}
                            {c.pickup ? ` · איסוף: ${c.pickup}` : ""}
                          </div>
                        </div>
                        <div className="shrink-0 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => loadFromSavedCard(c)}
                            className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs rounded px-2 py-1 transition"
                            data-testid={`ff-taxi-saved-load-${row?.id}-${idx}`}
                            title="טען את הפרטים לטופס"
                          >
                            טען
                          </button>
                          {c.id !== "legacy" && (
                            <button
                              type="button"
                              onClick={() => removeSavedCard(c.id)}
                              className="text-red-400 hover:text-red-300 rounded p-1 transition"
                              data-testid={`ff-taxi-saved-delete-${row?.id}-${idx}`}
                              title="מחק כרטיסייה"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {taxiVendors.length > 0 && savedCards.length === 0 && (
          <div className="text-xs text-sky-300 -mt-1" data-testid={`ff-taxi-auto-detected-${row?.id}`}>
            ✓ פרטי חברת המוניות מולאו אוטומטית מהוצאות ההפקה. ניתן למחוק ולהזין חדש.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 pt-1">
          <div>
            <label className={labelCls}>יום</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid={`ff-taxi-date-${row?.id}`} />
          </div>
          <div>
            <label className={labelCls}>שעה</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} data-testid={`ff-taxi-time-${row?.id}`} />
          </div>
          <div>
            <label className={labelCls}>חברת מוניות</label>
            <input type="text" value={taxiCompany} onChange={(e) => setTaxiCompany(e.target.value)} placeholder="לדוגמה: מוניות דן" className={inputCls} data-testid={`ff-taxi-company-${row?.id}`} />
          </div>
          <div>
            <label className={labelCls}>טלפון חברת המוניות</label>
            <input type="tel" value={taxiPhone} onChange={(e) => setTaxiPhone(e.target.value)} placeholder="050-1234567" dir="ltr" className={inputCls} data-testid={`ff-taxi-phone-${row?.id}`} />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>לוקיישן איסוף</label>
            <input type="text" value={pickup} onChange={(e) => setPickup(e.target.value)} placeholder="כתובת האיסוף" className={inputCls} data-testid={`ff-taxi-pickup-${row?.id}`} />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>לוקיישן הפקה (יעד)</label>
            <input type="text" value={dropoff} onChange={(e) => setDropoff(e.target.value)} placeholder="כתובת ההפקה" className={inputCls} data-testid={`ff-taxi-dropoff-${row?.id}`} />
          </div>
          <div>
            <label className={labelCls}>מספר נוסעים</label>
            <input type="number" min={1} value={pax} onChange={(e) => setPax(Number(e.target.value) || 1)} className={inputCls} data-testid={`ff-taxi-pax-${row?.id}`} />
          </div>
          <div />
          <div className="col-span-2">
            <label className={labelCls}>הערות (אופציונלי)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="כל הערה נוספת שתופיע בהודעה..." className={`${inputCls} resize-y leading-relaxed`} data-testid={`ff-taxi-notes-${row?.id}`} />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch gap-2 pt-3 border-t border-white/10 mt-2">
          <button
            type="button"
            onClick={saveCardDefaults}
            disabled={savingCard || !!savingKind}
            className="bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white font-extrabold text-sm rounded-md px-3 py-2 inline-flex items-center justify-center gap-1.5 transition"
            data-testid={`ff-taxi-save-card-${row?.id}`}
            title="שמור פרטים לשימוש הבא"
          >
            {savingCard ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            שמור פרטים
          </button>
          <button
            type="button"
            onClick={() => requestSend("crew")}
            disabled={!date || !time || !row?.phone || !!savingKind}
            className="flex-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:hover:bg-emerald-500 text-black font-extrabold text-sm rounded-md px-3 py-2 inline-flex items-center justify-center gap-1.5 transition"
            data-testid={`ff-taxi-send-crew-${row?.id}`}
            title="שלח לאיש הצוות בוואטסאפ"
          >
            <MessageCircle className="h-4 w-4" />
            שלח לאיש הצוות
          </button>
          <button
            type="button"
            onClick={() => requestSend("taxi")}
            disabled={!date || !time || !taxiPhone.trim() || !!savingKind}
            className="flex-1 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-40 disabled:hover:bg-yellow-500 text-black font-extrabold text-sm rounded-md px-3 py-2 inline-flex items-center justify-center gap-1.5 transition"
            data-testid={`ff-taxi-send-taxi-${row?.id}`}
            title="שלח לחברת המוניות בוואטסאפ"
          >
            <Car className="h-4 w-4" />
            שלח לחברת המוניות
          </button>
          <Button
            onClick={() => onOpenChange(false)}
            variant="outline"
            className="bg-transparent border-white/20 text-stone-200 hover:bg-white/10"
            data-testid={`ff-taxi-cancel-${row?.id}`}
          >
            סגור
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Confirmation modal — owner spec 2026-02-07 "תמיד שיהיה אישור" */}
    <Dialog open={!!confirm} onOpenChange={(v) => { if (!v && !savingKind) setConfirm(null); }}>
      <DialogContent dir="rtl" className="bg-stone-900 border-white/10 text-stone-100 sm:max-w-md" data-testid="ff-taxi-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="text-right">אישור שליחה {confirm?.title}</DialogTitle>
          <DialogDescription className="text-right text-stone-400 text-xs">
            תישלח הודעת WhatsApp למספר <span dir="ltr" className="font-bold text-emerald-300">{confirm?.phone}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="bg-stone-800/70 border border-white/10 rounded-md p-3 text-xs whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto" data-testid="ff-taxi-confirm-preview">
          {confirm?.message}
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10 mt-2">
          <Button
            onClick={() => setConfirm(null)}
            disabled={!!savingKind}
            variant="outline"
            className="bg-transparent border-white/20 text-stone-200 hover:bg-white/10"
            data-testid="ff-taxi-confirm-cancel"
          >
            ביטול
          </Button>
          <Button
            onClick={performSend}
            disabled={!!savingKind}
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold"
            data-testid="ff-taxi-confirm-send"
          >
            {savingKind ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> שולח...</>
            ) : (
              <><Send className="h-4 w-4 mr-1" /> שלח עכשיו</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
